'use client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
  type ReactNode
} from 'react';
import { useRouter } from 'next/router';
import { usePlanningContext } from './PlanningContext';
import { patchPlanningQuery } from '../../../lib/planning/queryPatch';
import {
  fetchPlanningFeedLightPage,
  type FetchPlanningFeedLightPageOptions,
  type PlanningFeedFacets
} from '../../../lib/services/applicationsApiService';
import {
  buildTypeaheadSuggestions,
  clampRadius,
  computeDistanceKm,
  extractPostcode,
  extractPostcodePrefix,
  formatRadiusLabel,
  normalizeText,
  isValidLatLng,
  parseAddressMetadata,
  type SearchSuggestion
} from '../../../lib/search/searchUtils';
import { searchPhoton, formatPhotonLabel } from '../../../lib/search/photon';
import { isPlanningReferenceQuery } from '../../../lib/planning/aiSearch';
import { hasApplicationFormData } from '../../../lib/planning/formData';
import { getSearchablePlanningApplicationText } from '../../../lib/planning/searchText';
import {
  formatPlanningTagLabel,
  getPlanningTagDetails,
  normalizePlanningTags
} from '../../../lib/planning/tags';
import {
  SHORT_LETS_CATEGORY_ID,
  isShortLetsSearchQuery
} from '../../../lib/planning/shortLetsSearch';
import { AGGREGATE_COUNCIL_SLUG, applicationIdentityKey } from '../../../lib/councils';
import { isLondonScope, londonPageWindow } from '../../../lib/services/londonPlanningService';
import { usesRetainedLondonDelivery } from '../../../lib/planning/londonSharedDelivery';
import { INTERACTIVE_FEED_PAGE_SIZE } from '../../../lib/planning/feedPerformance';
import {
  buildPlanningSearchAreaPath,
  getPlanningSearchRegionFromSuggestionId,
  getPlanningSearchRegionSuggestionId,
  parsePlanningSearchIntent,
  searchPlanningSearchRegions,
  shouldApplyPlanningSearchRegionDirectly,
  type PlanningSearchIntent
} from '../../../lib/planning/searchIntent';
import {
  countBucket,
  radiusBucket,
  textLengthBucket,
  trackPlanningUpdatesEvent,
  type PlanningUpdatesEventContext
} from '../../../lib/planningUpdatesAnalytics';
import type { PlanningAlertCriteria } from '../../../types/alerts';
import { normalizeCategory } from '../../../utils/categorizationHelper';
import { getCategoryById } from '../../../utils/projectCategories';

type LocationSource = 'dataset' | 'photon' | 'postcode' | 'fallback' | 'manual';
type LocationAccuracy = 'parcel' | 'street' | 'postcode' | 'city';
export type LocationKind = 'parcel' | 'street' | 'postcode' | 'city';
type DatePreset = '7d' | '30d' | '90d' | '180d' | 'year' | 'all' | 'custom';

export interface LocationFilterState {
  label: string;
  lat: number;
  lng: number;
  source: LocationSource;
  accuracy: LocationAccuracy;
  streetKey: string | null;
  houseNumber: string | null;
  kind: LocationKind;
}

export interface LocationMetadataDiagnostics {
  baseRadiusKm: number;
  appliedRadiusKm: number;
  mode: 'distance' | 'street' | 'parcel';
  matchesConsidered: number;
}

export interface LocationFilterDiagnostics {
  baseRadiusKm: number;
  appliedRadiusKm: number;
  mode: 'distance' | 'street' | 'parcel';
  matchesConsidered: number;
}

export interface RadiusSearchSummary {
  hasLocation: boolean;
  locationLabel: string | null;
  radiusKm: number;
  radiusLabel: string;
  exactResultCount: number;
  radiusResultCount: number;
  exactHasNoResultsButRadiusHasResults: boolean;
}

export interface PreparedPlanningSearchQuery {
  originalQuery: string;
  residualQuery: string;
  regionApplied: boolean;
  shortLetsApplied: boolean;
}

type LocationCreationInput = {
  label: string;
  lat: number;
  lng: number;
  source?: LocationSource;
  accuracy?: LocationAccuracy;
  streetKey?: string | null;
  houseNumber?: string | null;
};

const LOCATION_BASE_RADII: Record<LocationKind, number> = {
  parcel: 0.05,
  street: 0.2,
  postcode: 0.4,
  city: 1
};
const EXACT_ADDRESS_RADIUS_KM = 0.05;
const PLANNING_REGION_ROUTE_EVENT = 'cityscope:planning-region-route';

const deriveLocationKind = (
  accuracy: LocationAccuracy,
  streetKey: string | null,
  houseNumber: string | null
): LocationKind => {
  if (houseNumber) return 'parcel';
  if (accuracy === 'parcel') return 'parcel';
  if (streetKey) return 'street';
  if (accuracy === 'postcode') return 'postcode';
  return 'city';
};

export const createLocationState = (input: LocationCreationInput): LocationFilterState => {
  const fallbackMeta = parseAddressMetadata(input.label);
  const streetKey = input.streetKey !== undefined ? input.streetKey : fallbackMeta.streetKey;
  const houseNumber = input.houseNumber !== undefined ? input.houseNumber : fallbackMeta.houseNumber;
  const inferredAccuracy: LocationAccuracy = houseNumber
    ? 'parcel'
    : streetKey
      ? 'street'
      : input.source === 'postcode'
        ? 'postcode'
        : 'city';

  const accuracy = input.accuracy ?? inferredAccuracy;
  const kind = deriveLocationKind(accuracy, streetKey ?? null, houseNumber ?? null);

  return {
    label: input.label,
    lat: input.lat,
    lng: input.lng,
    source: input.source ?? 'manual',
    accuracy,
    streetKey: streetKey ?? null,
    houseNumber: houseNumber ?? null,
    kind
  };
};

const normalizeLocationState = (value: LocationFilterState | null): LocationFilterState | null => {
  if (!value) return null;
  return createLocationState({
    label: value.label,
    lat: value.lat,
    lng: value.lng,
    source: value.source,
    accuracy: value.accuracy,
    streetKey: 'streetKey' in value ? value.streetKey : undefined,
    houseNumber: 'houseNumber' in value ? value.houseNumber : undefined
  });
};

const _getBaseRadiusKm = (location: LocationFilterState): number => LOCATION_BASE_RADII[location.kind];

const titleCase = (value: string): string => value.split(' ').map(token => token ? token[0].toUpperCase() + token.slice(1) : '').join(' ');

const _toDisplayStreet = (streetKey: string | null): string | null => streetKey ? titleCase(streetKey) : null;

const _formatHouseNumber = (houseNumber: string | null): string | null => houseNumber ? houseNumber.toUpperCase() : null;

const SEARCH_STREET_TERMS = [
  'street',
  'road',
  'avenue',
  'ave',
  'lane',
  'ln',
  'place',
  'terrace',
  'crescent',
  'cres',
  'square',
  'sq',
  'mews',
  'wynd',
  'close',
  'row',
  'way',
  'walk',
  'drive',
  'dr',
  'park',
  'grove',
  'hill',
  'green',
  'gardens',
  'court',
  'circus',
  'parade',
];
const SEARCH_POSTCODE_REGEX = /[a-z]{1,2}\d[a-z\d]?\s?\d[a-z]{2}/i;
const escapeSearchRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const isAddressishSearchTerm = (term: string): boolean => {
  if (!term) return false;
  if (SEARCH_POSTCODE_REGEX.test(term)) return true;
  if (/^\d+[a-z]?$/.test(term)) return true;
  return SEARCH_STREET_TERMS.some(streetTerm => term.includes(streetTerm));
};
const buildSearchTermMatchers = (terms: string[]) => terms.map(term => ({
  term,
  addressish: isAddressishSearchTerm(term),
  pattern: new RegExp(`(^|[^a-z0-9])${escapeSearchRegex(term)}[a-z0-9]*([^a-z0-9]|$)`),
}));

interface FacetState {
  categories: string[];
  tags: string[];
  statuses: string[];
  datePreset: DatePreset;
  dateFrom: string | null;
  dateTo: string | null;
  impactMin: number | null;
  impactMax: number | null;
  hasDocuments: boolean | null;
  hasForms: boolean | null;
  postcodePrefixes: string[];
}

interface MapFilterState {
  status: string;
  scale: string;
  scaleMin: number;
  scaleMax: number;
  category: string;
  dateFilter: string;
  dateFrom: string;
  dateTo: string;
  includeWithdrawns: boolean;
  showOnlyFavorites: boolean;
  showOnlyWorthWatching: boolean;
}

const DEFAULT_MAP_FILTERS: MapFilterState = {
  status: 'All',
  scale: 'All',
  scaleMin: 1,
  scaleMax: 10,
  category: 'All',
  dateFilter: 'all',
  dateFrom: '',
  dateTo: '',
  includeWithdrawns: true,
  showOnlyFavorites: false,
  showOnlyWorthWatching: false
};

interface PreparedApplication {
  raw: Record<string, unknown>;
  normalizedText: string;
  normalizedAddress: string;
  addressTokens: string[];
  lat: number | null;
  lng: number | null;
  category: string | null;
  tags: string[];
  status: string | null;
  postcode: string | null;
  outcode: string | null;
  impact: number | null;
  hasDocuments: boolean;
  hasForms: boolean;
  createdAt: number | null;
  receivedAt: number | null;
  scrapedAt: number | null;
  validatedAt: number | null;
  reference: string;
  streetKey: string | null;
  houseNumber: string | null;
  distanceKm: number | null;
}

export interface FilteredApplication extends PreparedApplication {
  distanceKm: number | null;
}

export interface PlanningSearchResult extends Record<string, unknown> {
  reference_no: string;
  description?: string;
  ai_summary?: string;
  address?: string;
  category?: string | null;
  status?: string | null;
  documents?: unknown[];
  has_application_form?: boolean;
  received_date?: string;
  scraped_date?: string;
  validated_date?: string;
  link?: string;
  distanceKm: number | null;
}

interface SavedSearch {
  id: string;
  name: string;
  createdAt: string;
  tokens: string[];
  location: LocationFilterState | null;
  radiusKm: number;
  facetState: FacetState;
  mapPreview: boolean;
  alertsEnabled?: boolean;
}

interface ActiveChip {
  id: string;
  label: string;
  category: 'token' | 'location' | 'category' | 'tag' | 'status' | 'date' | 'impact' | 'documents' | 'forms' | 'postcode';
  description?: string;
  onRemove: () => void;
}

interface FacetCounts {
  categories: Array<{ value: string; count: number }>;
  tags: Array<{ value: string; count: number; label: string }>;
  statuses: Array<{ value: string; count: number }>;
  postcodePrefixes: Array<{ value: string; count: number }>;
  documents: { withDocuments: number; withoutDocuments: number };
  forms: { withForms: number; withoutForms: number };
}

interface PlanningSearchContextValue {
  searchInput: string;
  settledSearchInput: string;
  setSearchInput: (value: string) => void;
  searchTokens: string[];
  applyDeterministicSearchIntent: (value: string) => PreparedPlanningSearchQuery;
  addToken: (value: string) => void;
  removeToken: (value: string) => void;
  clearTokens: () => void;
  suggestions: SearchSuggestion[];
  selectSuggestion: (suggestion: SearchSuggestion) => void;
  location: LocationFilterState | null;
  setLocation: (value: LocationCreationInput | LocationFilterState | null) => void;
  radiusKm: number;
  setRadius: (value: number) => void;
  mapPreview: boolean;
  toggleMapPreview: () => void;
  facetState: FacetState;
  setFacetState: React.Dispatch<React.SetStateAction<FacetState>>;
  toggleCategory: (value: string, forceInclude?: boolean) => void;
  toggleTag: (value: string, forceInclude?: boolean) => void;
  setTags: (values: string[]) => void;
  clearTags: () => void;
  toggleStatus: (value: string, forceInclude?: boolean) => void;
  setDatePreset: (preset: DatePreset) => void;
  setCustomDateRange: (from: string | null, to: string | null) => void;
  setImpactRange: (min: number | null, max: number | null) => void;
  setHasDocuments: (value: boolean | null) => void;
  setHasForms: (value: boolean | null) => void;
  togglePostcode: (value: string, forceInclude?: boolean) => void;
  clearAll: () => void;
  results: PlanningSearchResult[];
  resultsWithMeta: FilteredApplication[];
  resultCount: number;
  isRemoteSearchLoading: boolean;
  isRemoteSearchLoadingMore: boolean;
  remoteSearchError: string | null;
  hasRemoteSearchResultSet: boolean;
  hasMoreRemoteSearchResults: boolean;
  loadMoreRemoteSearchResults: () => void;
  facetCounts: FacetCounts;
  activeChips: ActiveChip[];
  locationDiagnostics: LocationMetadataDiagnostics | null;
  radiusSearchSummary: RadiusSearchSummary;
  buildCurrentAlertCriteria: () => PlanningAlertCriteria | null;
  saveCurrentSearch: (name: string, options?: { enableAlerts?: boolean }) => void;
  savedSearches: SavedSearch[];
  deleteSavedSearch: (id: string) => void;
  applySavedSearch: (id: string) => void;
  alertsEnabled: boolean;
  setAlertsEnabled: (value: boolean) => void;
  pendingGeocode: boolean;
  geocodeError: string | null;
  requestGeocode: (value: string) => Promise<void>;
  mapFilters: MapFilterState;
  setMapStatusFilter: (value: string) => void;
  setMapScaleFilter: (value: string) => void;
  setMapScaleRange: (min: number, max: number) => void;
  setMapCategoryFilter: (value: string) => void;
  setMapDateFilter: (value: string) => void;
  setMapCustomDateRange: (from: string, to: string) => void;
  setMapIncludeWithdrawns: (value: boolean) => void;
  setMapShowOnlyFavorites: (value: boolean) => void;
  setMapShowOnlyWorthWatching: (value: boolean) => void;
  resetMapFilters: () => void;
}

const SEARCH_QUERY_KEYS = [
  'q',
  'search',
  'loc',
  'lat',
  'lng',
  'radius',
  'categories',
  'tags',
  'statuses',
  'datePreset',
  'from',
  'to',
  'impact',
  'docs',
  'forms',
  'postcodes',
  'map'
];
const ROUTE_QUERY_KEYS = ['reference', 'referenceSegments', 'councilCode'];
// Re-enable URL syncing with throttling to avoid shallow-route churn that causes UI flicker.
const URL_SYNC_ENABLED = true;
const URL_SYNC_DEBOUNCE_MS = 500;

const SAVED_SEARCH_STORAGE_KEY = 'cs_saved_searches_v2';
const getSavedSearchStorageKey = (councilSlug?: string) =>
  `${SAVED_SEARCH_STORAGE_KEY}:${councilSlug || 'edinburgh'}`;

const ensureArrayValue = (value?: string | string[]): string => {
  if (Array.isArray(value)) {
    return value[0] ?? '';
  }
  return value ?? '';
};

const decodeQueryPart = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch (_error) {
    return value;
  }
};

const stripRouteQueryKeys = (
  query: Record<string, string | string[] | undefined>
): Record<string, string | string[] | undefined> => {
  const clean = { ...query };
  ROUTE_QUERY_KEYS.forEach(key => {
    delete clean[key];
  });
  return clean;
};

const getRuntimeSearchQuery = (
  routerQuery: Record<string, string | string[] | undefined>
): Record<string, string | string[] | undefined> => {
  const merged = stripRouteQueryKeys(routerQuery);
  if (typeof window === 'undefined') return merged;

  const params = new URLSearchParams(window.location.search);
  [...SEARCH_QUERY_KEYS, 'tokens', 'category'].forEach(key => {
    const value = params.get(key);
    if (value !== null) {
      merged[key] = value;
    } else {
      // window.history.replaceState is used for low-churn filter updates, so
      // Next's router.query can legitimately lag behind the visible URL. An
      // absent runtime parameter must remove that stale router value rather
      // than silently rehydrating a filter the user just replaced.
      delete merged[key];
    }
  });
  return merged;
};

const getRuntimePathname = (routerPathname?: string): string => {
  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return routerPathname ?? '';
};

const parseListParam = (value?: string | string[]): string[] => {
  const raw = ensureArrayValue(value);
  if (!raw) return [];
  return raw
    .split(',')
    .map(part => decodeQueryPart(part.trim()))
    .filter(Boolean);
};

const encodeListParam = (value: string[]): string | null => {
  if (!value || value.length === 0) return null;
  return value.map(part => part.trim()).filter(Boolean).join(',');
};

const normalizeRuntimeSearchUrl = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const rawSearchValue = url.searchParams.get('q')
    ?? url.searchParams.get('tokens')
    ?? url.searchParams.get('search')
    ?? '';
  if (!rawSearchValue) return;

  const normalizedSearchValue = encodeListParam(parseListParam(rawSearchValue));
  if (!normalizedSearchValue || rawSearchValue === normalizedSearchValue) return;

  url.searchParams.set('q', normalizedSearchValue);
  url.searchParams.delete('search');
  url.searchParams.delete('tokens');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

const parseNumberParam = (value?: string | string[], fallback: number | null = null): number | null => {
  const raw = ensureArrayValue(value);
  if (!raw) return fallback;
  const num = Number(raw);
  return Number.isFinite(num) ? num : fallback;
};

const parseBooleanParam = (value?: string | string[]): boolean | null => {
  const raw = ensureArrayValue(value).toLowerCase();
  if (raw === '1' || raw === 'true') return true;
  if (raw === '0' || raw === 'false') return false;
  return null;
};

const parseImpactParam = (value?: string | string[]): { min: number | null; max: number | null } => {
  const raw = ensureArrayValue(value);
  if (!raw) return { min: null, max: null };
  const [minRaw, maxRaw] = raw.split('-');
  const min = minRaw !== undefined && minRaw !== '' ? Number(minRaw) : null;
  const max = maxRaw !== undefined && maxRaw !== '' ? Number(maxRaw) : null;
  return {
    min: Number.isFinite(min as number) ? (min as number) : null,
    max: Number.isFinite(max as number) ? (max as number) : null
  };
};

const computeQuerySignature = (params: Record<string, string | null | undefined>): string => {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => [key, value === null ? null : String(value)] as const)
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
};

const toTimestamp = (value?: string | null): number | null => {
  if (!value) return null;
  const date = new Date(value);
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : null;
};

const getMostRelevantDateMs = (app: PreparedApplication): number | null => {
  return app.createdAt ?? app.scrapedAt ?? app.validatedAt ?? app.receivedAt;
};

const getDateRange = (preset: DatePreset, from: string | null, to: string | null): { start: number | null; end: number | null } => {
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  switch (preset) {
    case '7d':
      return { start: now - 7 * dayMs, end: now };
    case '30d':
      return { start: now - 30 * dayMs, end: now };
    case '90d':
      return { start: now - 90 * dayMs, end: now };
    case '180d':
      return { start: now - 180 * dayMs, end: now };
    case 'year':
      return { start: now - 365 * dayMs, end: now };
    case 'custom': {
      const start = toTimestamp(from);
      const end = toTimestamp(to);
      return { start, end };
    }
    default:
      return { start: null, end: null };
  }
};

const filterApplicationsByLocationRadius = (
  applications: PreparedApplication[],
  currentLocation: LocationFilterState | null,
  currentRadiusKm: number
): FilteredApplication[] => {
  if (!currentLocation || !isValidLatLng(currentLocation.lat, currentLocation.lng)) {
    return applications;
  }

  const effectiveRadius = currentRadiusKm === 0 ? EXACT_ADDRESS_RADIUS_KM : currentRadiusKm;
  return applications.reduce<FilteredApplication[]>((acc, item) => {
    if (!isValidLatLng(item.lat, item.lng)) {
      return acc;
    }

    const distanceKmValue = computeDistanceKm(
      currentLocation.lat,
      currentLocation.lng,
      item.lat as number,
      item.lng as number
    );

    if (distanceKmValue <= effectiveRadius) {
      acc.push({ ...item, distanceKm: distanceKmValue });
    }

    return acc;
  }, []);
};

const buildLocationQueryOptions = (
  currentLocation: LocationFilterState | null,
  currentRadiusKm: number
): Pick<FetchPlanningFeedLightPageOptions, 'bounds' | 'centerLat' | 'centerLng' | 'radiusKm'> => {
  if (!currentLocation || !isValidLatLng(currentLocation.lat, currentLocation.lng)) {
    return {};
  }

  const effectiveRadiusKm = currentRadiusKm === 0 ? EXACT_ADDRESS_RADIUS_KM : currentRadiusKm;
  const latitudeDelta = effectiveRadiusKm / 111.32;
  const longitudeScale = Math.max(Math.cos((currentLocation.lat * Math.PI) / 180), 0.01);
  const longitudeDelta = effectiveRadiusKm / (111.32 * longitudeScale);

  return {
    bounds: {
      min_lng: currentLocation.lng - longitudeDelta,
      min_lat: currentLocation.lat - latitudeDelta,
      max_lng: currentLocation.lng + longitudeDelta,
      max_lat: currentLocation.lat + latitudeDelta,
    },
    centerLat: currentLocation.lat,
    centerLng: currentLocation.lng,
    radiusKm: effectiveRadiusKm,
  };
};

const mergeRemoteApplications = (
  current: Record<string, unknown>[],
  incoming: Record<string, unknown>[]
): Record<string, unknown>[] => {
  const merged = new Map<string, Record<string, unknown>>();
  [...current, ...incoming].forEach((application, index) => {
    const identity = applicationIdentityKey(application) || `remote:${index}`;
    if (!merged.has(identity)) merged.set(identity, application);
  });
  return Array.from(merged.values());
};

const normalizeRemoteApplication = (
  application: Record<string, unknown>
): Record<string, unknown> => {
  const rawCategory = typeof application.category === 'string'
    ? application.category.trim()
    : '';
  if (!rawCategory) {
    return { ...application, category: 'uncategorized' };
  }

  return {
    ...application,
    category: normalizeCategory(rawCategory),
    originalCategory: rawCategory
  };
};

const normalizeRemoteApplications = (applications: Record<string, unknown>[]) => (
  Array.isArray(applications) ? applications.map(normalizeRemoteApplication) : []
);

const filterApplicationsByFacets = (
  applications: FilteredApplication[],
  state: FacetState,
  dateStart: number | null,
  dateEnd: number | null
): FilteredApplication[] => applications.filter(item => {
  if (state.categories.length > 0) {
    if (!item.category || !state.categories.includes(item.category)) {
      return false;
    }
  }

  if (state.tags.length > 0) {
    if (!item.tags.some((tag) => state.tags.includes(tag))) {
      return false;
    }
  }

  if (state.statuses.length > 0) {
    if (!item.status || !state.statuses.includes(item.status)) {
      return false;
    }
  }

  if (state.postcodePrefixes.length > 0) {
    if (!item.outcode || !state.postcodePrefixes.some(prefix => item.outcode?.startsWith(prefix))) {
      return false;
    }
  }

  if (state.hasDocuments !== null) {
    if (state.hasDocuments && !item.hasDocuments) return false;
    if (state.hasDocuments === false && item.hasDocuments) return false;
  }

  if (state.hasForms !== null) {
    if (state.hasForms && !item.hasForms) return false;
    if (state.hasForms === false && item.hasForms) return false;
  }

  if (state.impactMin !== null || state.impactMax !== null) {
    if (item.impact === null) return false;
    if (state.impactMin !== null && item.impact < state.impactMin) return false;
    if (state.impactMax !== null && item.impact > state.impactMax) return false;
  }

  if (dateStart || dateEnd) {
    const relevant = getMostRelevantDateMs(item);
    if (!relevant) return false;
    if (dateStart && relevant < dateStart) return false;
    if (dateEnd && relevant > dateEnd) return false;
  }

  return true;
});

const formatDateLabel = (preset: DatePreset, from: string | null, to: string | null): string => {
  switch (preset) {
    case '7d':
      return 'Last 7 days';
    case '30d':
      return 'Last 30 days';
    case '90d':
      return 'Last 90 days';
    case '180d':
      return 'Last 180 days';
    case 'year':
      return 'Past year';
    case 'custom':
      if (from && to) return `${from} to ${to}`;
      if (from) return `From ${from}`;
      if (to) return `Until ${to}`;
      return 'Custom range';
    default:
      return 'Any time';
  }
};

const createEmptyFacetState = (): FacetState => ({
  categories: [],
  tags: [],
  statuses: [],
  datePreset: 'all',
  dateFrom: null,
  dateTo: null,
  impactMin: null,
  impactMax: null,
  hasDocuments: null,
  hasForms: null,
  postcodePrefixes: []
});

const countActiveFacetFilters = (state: FacetState): number => (
  state.categories.length
  + state.tags.length
  + state.statuses.length
  + state.postcodePrefixes.length
  + (state.datePreset !== 'all' || state.dateFrom || state.dateTo ? 1 : 0)
  + (state.impactMin !== null || state.impactMax !== null ? 1 : 0)
  + (state.hasDocuments !== null ? 1 : 0)
  + (state.hasForms !== null ? 1 : 0)
);

const countActivePlanningSearchControls = (
  tokens: string[],
  currentLocation: LocationFilterState | null,
  currentRadiusKm: number,
  state: FacetState
): number => (
  tokens.length
  + (currentLocation ? 1 : 0)
  + (currentRadiusKm > 0 ? 1 : 0)
  + countActiveFacetFilters(state)
);

export const PlanningSearchContext = createContext<PlanningSearchContextValue | undefined>(undefined);

const ABBREVIATION_VARIANTS: Array<[RegExp, string]> = [
  [/\bst\b/g, 'saint'],
  [/\bsaint\b/g, 'st'],
  [/\bstreet\b/g, 'st'],
  [/\brd\b/g, 'road'],
  [/\broad\b/g, 'rd'],
  [/\bave\b/g, 'avenue'],
  [/\bavenue\b/g, 'ave'],
  [/\bblvd\b/g, 'boulevard'],
  [/\bblvd\b/g, 'boulevard'],
  [/\bln\b/g, 'lane'],
  [/\blane\b/g, 'ln'],
  [/\bgdns\b/g, 'gardens'],
  [/\bgardens\b/g, 'gdns'],
  [/\bsq\b/g, 'square'],
  [/\bsquare\b/g, 'sq'],
  [/\bpl\b/g, 'place'],
  [/\bplc\b/g, 'place'],
  [/\bplace\b/g, 'pl'],
  [/\bctr\b/g, 'centre'],
  [/\bcenter\b/g, 'centre'],
  [/\bcentre\b/g, 'ctr']
];


const generateLocationVariants = (value: string): string[] => {
  const variants = new Set<string>();
  variants.add(value);
  ABBREVIATION_VARIANTS.forEach(([regex, replacement]) => {
    if (regex.test(value)) {
      const replaced = value.replace(regex, replacement);
      variants.add(replaced);
    }
  });
  // Also expand common punctuation by removing spaces to catch collapsed dataset tokens
  variants.add(value.replace(/\s+/g, ' '));
  return Array.from(variants);
};

const bigrams = (value: string): string[] => {
  const cleaned = value.replace(/[^a-z0-9\s]/g, '');
  const pairs: string[] = [];
  for (let i = 0; i < cleaned.length - 1; i += 1) {
    const pair = cleaned.slice(i, i + 2);
    if (!pair.includes(' ')) pairs.push(pair);
  }
  return pairs;
};

const diceCoefficient = (a: string, b: string): number => {
  if (!a || !b) return 0;
  const aBigrams = bigrams(a);
  const bBigrams = bigrams(b);
  if (aBigrams.length === 0 || bBigrams.length === 0) return 0;
  const map = new Map<string, number>();
  aBigrams.forEach(pair => map.set(pair, (map.get(pair) ?? 0) + 1));
  let intersection = 0;
  bBigrams.forEach(pair => {
    const count = map.get(pair) ?? 0;
    if (count > 0) {
      intersection += 1;
      map.set(pair, count - 1);
    }
  });
  return (2 * intersection) / (aBigrams.length + bBigrams.length);
};

const DATASET_MATCH_THRESHOLD = 0.75;
// Keep interactive result pages comfortably ahead of the virtualized viewport
// without making every query reconcile hundreds of rich card records at once.
const REMOTE_SEARCH_PAGE_SIZE = INTERACTIVE_FEED_PAGE_SIZE;
const REMOTE_SEARCH_DEBOUNCE_MS = 220;
const LONDON_TIME_ZONE = 'Europe/London';
const REMOTE_CATEGORY_LABEL_OVERRIDES: Record<string, string> = {
  renewables_energy: 'Renewable Energy, EV & Heat Pumps'
};

const getRemoteCategoryLabel = (categoryId: unknown) => {
  const cleaned = String(categoryId || '').replace('__low', '').trim();
  if (!cleaned) return '';
  return REMOTE_CATEGORY_LABEL_OVERRIDES[cleaned]
    || getCategoryById(cleaned)?.label
    || cleaned;
};

const isShortLetsCategory = (value: unknown) => (
  String(value || '').trim().toLowerCase().replace(/[-_\s]+/g, '') === 'shorttermlets'
);

const londonDateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: LONDON_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

const getLondonDateTimeParts = (value: Date) => {
  const parts = londonDateTimeFormatter.formatToParts(value);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(
    parts.find((part) => part.type === type)?.value ?? 0
  );
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second')
  };
};

const londonDateTimeToUtc = (
  year: number,
  month: number,
  day: number,
  hour: number
): Date => {
  const targetTimestamp = Date.UTC(year, month - 1, day, hour, 0, 0);
  let candidateTimestamp = targetTimestamp;

  for (let iteration = 0; iteration < 3; iteration += 1) {
    const actual = getLondonDateTimeParts(new Date(candidateTimestamp));
    const actualTimestamp = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second
    );
    const correction = targetTimestamp - actualTimestamp;
    candidateTimestamp += correction;
    if (correction === 0) break;
  }

  return new Date(candidateTimestamp);
};

const getLondonAddedWindow = (referenceDate: Date, dayOffset: number) => {
  const referenceParts = getLondonDateTimeParts(referenceDate);
  const targetDay = new Date(Date.UTC(
    referenceParts.year,
    referenceParts.month - 1,
    referenceParts.day + dayOffset
  ));
  const nextDay = new Date(Date.UTC(
    targetDay.getUTCFullYear(),
    targetDay.getUTCMonth(),
    targetDay.getUTCDate() + 1
  ));

  return {
    from: londonDateTimeToUtc(
      targetDay.getUTCFullYear(),
      targetDay.getUTCMonth() + 1,
      targetDay.getUTCDate(),
      6
    ).toISOString(),
    to: londonDateTimeToUtc(
      nextDay.getUTCFullYear(),
      nextDay.getUTCMonth() + 1,
      nextDay.getUTCDate(),
      0
    ).toISOString()
  };
};

const getUtcAddedWindow = (dateValue: string) => {
  const parsed = new Date(`${dateValue}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    from: parsed.toISOString(),
    to: new Date(parsed.getTime() + 24 * 60 * 60 * 1000).toISOString()
  };
};

const getLegacyScaleRange = (
  selectedScaleCategory: string,
  scaleRange: { min?: number; max?: number } | null | undefined
) => {
  const rawMin = Number(scaleRange?.min);
  const rawMax = Number(scaleRange?.max);
  const normalizedMin = Number.isFinite(rawMin) ? rawMin : 1;
  const normalizedMax = Number.isFinite(rawMax) ? rawMax : 10;
  const min = Math.max(1, Math.min(normalizedMin, normalizedMax));
  const max = Math.min(10, Math.max(normalizedMin, normalizedMax));
  if (min > 1 || max < 10) return { min, max };

  const presets: Record<string, { min: number; max: number }> = {
    low: { min: 1, max: 3 },
    small: { min: 1, max: 3 },
    medium: { min: 4, max: 6 },
    high: { min: 7, max: 10 },
    large: { min: 7, max: 10 }
  };
  return presets[selectedScaleCategory] ?? null;
};

const getRecordString = (value: Record<string, unknown>, key: string): string | null => {
  const raw = value?.[key];
  return typeof raw === 'string' ? raw : null;
};

export const PlanningSearchProvider = ({
  children,
  skipUnfilteredRemoteBootstrap = false,
}: {
  children: ReactNode;
  skipUnfilteredRemoteBootstrap?: boolean;
}) => {
  const router = useRouter();
  const {
    allApplications = [],
    areaId,
    councilConfig,
    councilSlug,
    categoryFilterMode = 'include',
    decisionStatusFilter = 'all',
    filterByAddedToday,
    filterByAddedYesterday,
    filterByScrapedDate,
    multipleCategoryFilters = [],
    referenceDate,
    scaleRange,
    selectedCategoryFilter,
    selectedFilter = 'all',
    selectedScaleCategory = 'all',
    setCategoryFilterMode,
    setMultipleCategoryFilters,
    setSearchQuery,
    setSelectedCategoryFilter,
    switchCouncil
  } = usePlanningContext();

  const baseApplications = useMemo(() => (Array.isArray(allApplications) ? allApplications : []), [allApplications]);
  const baseApplicationsRef = useRef(baseApplications);
  useEffect(() => {
    baseApplicationsRef.current = baseApplications;
  }, [baseApplications]);
  const defaultLocation: LocationFilterState = useMemo(() => createLocationState({
    label: councilConfig?.displayName || 'City Centre',
    lat: councilConfig?.defaultCenter?.lat ?? 55.953251,
    lng: councilConfig?.defaultCenter?.lng ?? -3.188267,
    source: 'fallback',
    accuracy: 'city'
  }), [councilConfig]);

  const [searchInput, setSearchInput] = useState('');
  const [searchTokens, setSearchTokens] = useState<string[]>([]);
  const [location, setLocationState] = useState<LocationFilterState | null>(null);
  const [radiusKm, setRadiusKm] = useState<number>(0);
  const [facetState, setFacetState] = useState<FacetState>(() => createEmptyFacetState());
  const [mapPreview, setMapPreview] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [mapFilters, setMapFilters] = useState<MapFilterState>(() => ({ ...DEFAULT_MAP_FILTERS }));
  const [pendingGeocode, setPendingGeocode] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [hydratedFromQuery, setHydratedFromQuery] = useState(false);
  const [settledSearchInput, setSettledSearchInput] = useState('');
  const [remoteSearchState, setRemoteSearchState] = useState<{
    signature: string;
    applications: Record<string, unknown>[];
    total: number;
    nextCursor: string | null;
    facets: PlanningFeedFacets;
  }>({ signature: '', applications: [], total: 0, nextCursor: null, facets: {} });
  const [isRemoteSearchLoading, setIsRemoteSearchLoading] = useState(false);
  const [isRemoteSearchLoadingMore, setIsRemoteSearchLoadingMore] = useState(false);
  const [remoteSearchError, setRemoteSearchError] = useState<string | null>(null);
  const remoteRequestOptionsRef = useRef<FetchPlanningFeedLightPageOptions | null>(null);
  const lastQuerySignatureRef = useRef<string>('');
  const lastHydratedRuntimeUrlRef = useRef<string>('');
  const pendingQueryUpdateRef = useRef<number | null>(null);
  const photonAbortControllerRef = useRef<AbortController | null>(null);
  const hydratedTokenSignatureRef = useRef<string | null>(null);
  const hydratedTagSignatureRef = useRef<string | null>(null);
  const streetTokenRef = useRef<string | null>(null);
  const lastTypedRef = useRef<string>('');
  const searchStartedTrackedRef = useRef(false);
  const lastSearchInputBucketRef = useRef<string>('0');
  const lastResultsAnalyticsSignatureRef = useRef<string>('');
  const updateSearchInput = useCallback((value: string) => {
    const trimmed = value.trim();
    const inputLengthBucket = textLengthBucket(trimmed);

    if (trimmed) {
      if (!searchStartedTrackedRef.current) {
        searchStartedTrackedRef.current = true;
        trackPlanningUpdatesEvent('search_started', {
          placement: 'search_input',
          status: inputLengthBucket,
          metadata: {
            input_length_bucket: inputLengthBucket
          }
        });
      }

      if (inputLengthBucket !== lastSearchInputBucketRef.current) {
        lastSearchInputBucketRef.current = inputLengthBucket;
        trackPlanningUpdatesEvent('planning_updates_search_input_changed', {
          placement: 'search_input',
          status: inputLengthBucket,
          metadata: {
            input_length_bucket: inputLengthBucket
          }
        });
      }
    } else {
      lastSearchInputBucketRef.current = '0';
    }

    setSearchInput(value);
    lastTypedRef.current = value;
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSettledSearchInput(searchInput);
    }, REMOTE_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const remoteSearchQuery = useMemo(() => {
    const values = [...searchTokens, settledSearchInput]
      .map((value) => value.trim())
      .filter(Boolean);
    return Array.from(new Set(values)).join(' ').trim();
  }, [searchTokens, settledSearchInput]);

  const hasRemoteFilters = useMemo(() => (
    Boolean(location) ||
    radiusKm > 0 ||
    facetState.categories.length > 0 ||
    facetState.tags.length > 0 ||
    facetState.statuses.length > 0 ||
    facetState.postcodePrefixes.length > 0 ||
    facetState.datePreset !== 'all' ||
    Boolean(facetState.dateFrom || facetState.dateTo) ||
    facetState.impactMin !== null ||
    facetState.impactMax !== null ||
    facetState.hasDocuments !== null ||
    facetState.hasForms !== null
  ), [facetState, location, radiusKm]);

  const remoteLocationOptions = useMemo(
    () => buildLocationQueryOptions(location, radiusKm),
    [location, radiusKm]
  );
  const remoteDateRange = useMemo(
    () => getDateRange(facetState.datePreset, facetState.dateFrom, facetState.dateTo),
    [facetState.dateFrom, facetState.datePreset, facetState.dateTo]
  );
  const legacyCategoryValues = useMemo(() => {
    const values = Array.isArray(multipleCategoryFilters) && multipleCategoryFilters.length > 0
      ? multipleCategoryFilters
      : (selectedCategoryFilter ? [selectedCategoryFilter] : []);
    return Array.from(new Set(
      values
        .map(getRemoteCategoryLabel)
        .filter(Boolean)
    ));
  }, [multipleCategoryFilters, selectedCategoryFilter]);
  const legacyScaleRange = useMemo(
    () => getLegacyScaleRange(selectedScaleCategory, scaleRange),
    [scaleRange, selectedScaleCategory]
  );
  const legacyAddedWindow = useMemo(() => {
    const effectiveReferenceDate = referenceDate instanceof Date && !Number.isNaN(referenceDate.getTime())
      ? referenceDate
      : new Date();
    if (filterByScrapedDate) return getUtcAddedWindow(String(filterByScrapedDate));
    if (filterByAddedToday || selectedFilter === 'added-today') {
      return getLondonAddedWindow(effectiveReferenceDate, 0);
    }
    if (filterByAddedYesterday) return getLondonAddedWindow(effectiveReferenceDate, -1);
    if (['new', 'recent', 'weekly-highlights', 'trending'].includes(selectedFilter)) {
      return {
        from: new Date(effectiveReferenceDate.getTime() - (7 * 24 * 60 * 60 * 1000)).toISOString(),
        to: null,
      };
    }
    return null;
  }, [filterByAddedToday, filterByAddedYesterday, filterByScrapedDate, referenceDate, selectedFilter]);
  const remoteInteresting = selectedFilter === 'worth-watching' ? true : null;
  const normalizedDecisionStatus = ['pending', 'decided', 'refused', 'withdrawn'].includes(
    String(decisionStatusFilter || '').toLowerCase()
  )
    ? String(decisionStatusFilter).toLowerCase() as 'pending' | 'decided' | 'refused' | 'withdrawn'
    : null;
  const hasLegacyRemoteFilters = Boolean(
    legacyCategoryValues.length > 0 ||
    legacyScaleRange ||
    normalizedDecisionStatus ||
    legacyAddedWindow ||
    remoteInteresting
  );
  const { remoteCategories, remoteExcludedCategories } = useMemo(() => ({
    remoteCategories: legacyCategoryValues.length > 0 && categoryFilterMode !== 'exclude'
      ? legacyCategoryValues
      : facetState.categories,
    remoteExcludedCategories: legacyCategoryValues.length > 0 && categoryFilterMode === 'exclude'
      ? legacyCategoryValues
      : []
  }), [categoryFilterMode, facetState.categories, legacyCategoryValues]);
  const londonScope = isLondonScope(councilSlug);
  const remoteRequestOptions = useMemo<FetchPlanningFeedLightPageOptions>(() => ({
      query: remoteSearchQuery || undefined,
      includeShortLets: !londonScope && (isShortLetsSearchQuery(remoteSearchQuery)
        || remoteCategories.some(isShortLetsCategory)),
      council: councilSlug || AGGREGATE_COUNCIL_SLUG,
      areaId: areaId || undefined,
      limit: REMOTE_SEARCH_PAGE_SIZE,
      categories: remoteCategories,
      excludeCategories: remoteExcludedCategories,
      tags: facetState.tags,
      statuses: facetState.statuses,
      postcodePrefixes: facetState.postcodePrefixes,
      hasDocuments: facetState.hasDocuments,
      hasForms: facetState.hasForms,
      decisionStatus: normalizedDecisionStatus,
      impactMin: legacyScaleRange?.min ?? facetState.impactMin,
      impactMax: legacyScaleRange?.max ?? facetState.impactMax,
      activityFrom: !londonScope && remoteDateRange.start ? new Date(remoteDateRange.start).toISOString() : null,
      activityTo: !londonScope && remoteDateRange.end ? new Date(remoteDateRange.end).toISOString() : null,
      // London source dates are calendar validation dates. The end picker is
      // inclusive; the source query uses an exclusive following day.
      validatedFrom: londonScope && remoteDateRange.start
        ? new Date(remoteDateRange.start).toISOString().slice(0, 10) : undefined,
      validatedEndExclusive: londonScope && remoteDateRange.end
        ? new Date(remoteDateRange.end + 86400000).toISOString().slice(0, 10) : undefined,
      addedFrom: legacyAddedWindow?.from ?? null,
      addedTo: legacyAddedWindow?.to ?? null,
      interesting: remoteInteresting,
      ...remoteLocationOptions,
      includeFacets: !usesRetainedLondonDelivery(councilSlug),
      retries: 0,
      timeoutMs: 15000,
    }), [
    areaId,
    councilSlug,
    facetState,
    legacyAddedWindow,
    legacyScaleRange,
    normalizedDecisionStatus,
    remoteCategories,
    remoteExcludedCategories,
    remoteDateRange.end,
    remoteDateRange.start,
    remoteLocationOptions,
    remoteInteresting,
    remoteSearchQuery,
    londonScope,
  ]);
  const remoteRequestSignature = useMemo(
    () => JSON.stringify(remoteRequestOptions),
    [remoteRequestOptions]
  );
  const remoteRequestSnapshotRef = useRef({
    signature: remoteRequestSignature,
    options: remoteRequestOptions,
  });
  remoteRequestSnapshotRef.current = {
    signature: remoteRequestSignature,
    options: remoteRequestOptions,
  };
  const isUnfilteredRemoteBrowse = !remoteSearchQuery && !hasRemoteFilters && !hasLegacyRemoteFilters;

  useEffect(() => {
    if (!hydratedFromQuery) return undefined;

    // The map owns its bounded newest/viewport feed. Starting the generic
    // planning-updates browse request as well duplicates work and can delay the
    // map on a cold read-model cache. Preserve remote authority as soon as the
    // user applies a search or filter.
    if (skipUnfilteredRemoteBootstrap && isUnfilteredRemoteBrowse) {
      remoteRequestOptionsRef.current = null;
      setIsRemoteSearchLoading(false);
      setIsRemoteSearchLoadingMore(false);
      setRemoteSearchError(null);
      return undefined;
    }

    const requestSnapshot = remoteRequestSnapshotRef.current;
    if (requestSnapshot.signature !== remoteRequestSignature) return undefined;

    remoteRequestOptionsRef.current = requestSnapshot.options;
    const controller = new AbortController();
    setIsRemoteSearchLoading(true);
    setIsRemoteSearchLoadingMore(false);
    setRemoteSearchError(null);

    fetchPlanningFeedLightPage({
      ...requestSnapshot.options,
      offset: 0,
      signal: controller.signal,
    })
      .then((page) => {
        if (controller.signal.aborted) return;
        if (usesRetainedLondonDelivery(requestSnapshot.options.council) && !page.partial) {
          const window = londonPageWindow(page, {
            start: requestSnapshot.options.validatedFrom,
            end: requestSnapshot.options.validatedEndExclusive,
          });
          remoteRequestOptionsRef.current = {
            ...requestSnapshot.options, validatedFrom: window.start, validatedEndExclusive: window.end,
          };
        }
        const normalizedApplications = normalizeRemoteApplications(page.applications);
        const applications = page.partial
          ? (isUnfilteredRemoteBrowse ? baseApplicationsRef.current : [])
          : isUnfilteredRemoteBrowse && !londonScope
          ? mergeRemoteApplications(baseApplicationsRef.current, normalizedApplications)
          : normalizedApplications;
        const total = page.partial
          ? applications.length
          : page.total;

        startTransition(() => {
          setRemoteSearchState({
            signature: remoteRequestSignature,
            applications,
            total,
            nextCursor: page.partial ? null : page.next_cursor,
            facets: page.facets,
          });
          setRemoteSearchError(null);
          setIsRemoteSearchLoading(false);
        });
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.kind === 'cancelled') return;
        setRemoteSearchState({ signature: '', applications: [], total: 0, nextCursor: null, facets: {} });
        setRemoteSearchError(usesRetainedLondonDelivery(requestSnapshot.options.council) && error?.kind === 'validation'
          ? 'London supports reference, address or proposal search and validation dates. This filter is not yet available; clear it to continue.'
          : 'Planning data is temporarily unavailable. Your filters have been kept; please retry shortly.');
        setIsRemoteSearchLoading(false);
      });

    return () => controller.abort();
  }, [
    hydratedFromQuery,
    isUnfilteredRemoteBrowse,
    londonScope,
    remoteRequestSignature,
    skipUnfilteredRemoteBootstrap,
  ]);

  const effectiveApplications = useMemo(() => (
    remoteRequestSignature && remoteSearchState.signature === remoteRequestSignature
      ? remoteSearchState.applications
      : baseApplications
  ), [baseApplications, remoteRequestSignature, remoteSearchState]);
  const hasRemoteSearchResultSet = Boolean(
    remoteRequestSignature && remoteSearchState.signature === remoteRequestSignature
  );
  const hasMoreRemoteSearchResults = Boolean(
    hasRemoteSearchResultSet && remoteSearchState.nextCursor
  );
  const loadMoreRemoteSearchResults = useCallback(() => {
    const options = remoteRequestOptionsRef.current;
    const cursor = remoteSearchState.nextCursor;
    if (!options || !cursor || isRemoteSearchLoadingMore || !hasRemoteSearchResultSet) return;

    setRemoteSearchError(null);
    setIsRemoteSearchLoadingMore(true);
    fetchPlanningFeedLightPage({
      ...options,
      cursor,
      offset: undefined,
      includeFacets: false,
      includeTotal: false,
    })
      .then((page) => {
        if (page.partial || page.next_cursor === cursor) throw new Error("Incomplete search page");
        if (usesRetainedLondonDelivery(options.council)) londonPageWindow(page, {
          start: options.validatedFrom, end: options.validatedEndExclusive,
        });
        startTransition(() => {
          setRemoteSearchState((current) => {
            if (current.signature !== remoteRequestSignature) return current;
            return {
              ...current,
              applications: mergeRemoteApplications(
                current.applications,
                normalizeRemoteApplications(page.applications)
              ),
              nextCursor: page.next_cursor,
            };
          });
          setIsRemoteSearchLoadingMore(false);
        });
      })
      .catch(() => {
        if (remoteRequestSnapshotRef.current.signature !== remoteRequestSignature) return;
        setRemoteSearchError("Some results could not be loaded. Please retry.");
        setIsRemoteSearchLoadingMore(false);
      });
  }, [
    hasRemoteSearchResultSet,
    isRemoteSearchLoadingMore,
    remoteRequestSignature,
    remoteSearchState.nextCursor,
  ]);

  const preparedApplications = useMemo<PreparedApplication[]>(() => {
    return effectiveApplications.map((app: Record<string, unknown>) => {
      const latRaw = app?.['latitude'] ?? app?.['lat'] ?? null;
      const lngRaw = app?.['longitude'] ?? app?.['lng'] ?? null;
      const lat = Number.isFinite(Number(latRaw)) ? Number(latRaw) : null;
      const lng = Number.isFinite(Number(lngRaw)) ? Number(lngRaw) : null;
      const address = getRecordString(app, 'address') ?? getRecordString(app, 'location') ?? '';
      const description = getRecordString(app, 'description') ?? '';
      const searchableText = getSearchablePlanningApplicationText(app);
      const reference = getRecordString(app, 'reference_no') ?? getRecordString(app, 'ref') ?? '';
      const category = getRecordString(app, 'originalCategory') ?? getRecordString(app, 'category');
      const tagDetails = getPlanningTagDetails(app);
      const rawTags = normalizePlanningTags(app?.['tags']);
      const tags = rawTags.length > 0
        ? rawTags
        : normalizePlanningTags(tagDetails.map((detail) => detail.slug));
      const status = getRecordString(app, 'status') ?? getRecordString(app, 'decision');
      const postcode = extractPostcode(address);
      const outcode = extractPostcodePrefix(address);
      const impactSource = app?.['scale_rating'];
      const impactValue = impactSource != null ? Number(impactSource) : null;
      const rawDocuments = app?.['documents'];
      const hasDocuments = Array.isArray(rawDocuments)
        ? rawDocuments.length > 0
        : app?.['has_documents'] === true;
      const hasForms = hasApplicationFormData(app);
      const normalizedAddress = normalizeText(address);
      const addressTokens = normalizedAddress.split(' ').filter(Boolean);
      const { streetKey, houseNumber } = parseAddressMetadata(address);
      const normalizedText = normalizeText([
        searchableText,
        address,
        description,
        reference,
        category ?? '',
        status ?? ''
      ].filter(Boolean).join(' '));

      return {
        raw: app,
        normalizedText,
        normalizedAddress,
        addressTokens,
        lat,
        lng,
        category,
        tags,
        status,
        postcode,
        outcode,
        impact: Number.isFinite(impactValue as number) ? (impactValue as number) : null,
        hasDocuments,
        hasForms,
        createdAt: toTimestamp(getRecordString(app, 'created_at')),
        receivedAt: toTimestamp(getRecordString(app, 'received_date')),
        scrapedAt: toTimestamp(getRecordString(app, 'scraped_date')),
        validatedAt: toTimestamp(getRecordString(app, 'validated_date')),
        reference: reference ?? '',
        streetKey,
        houseNumber,
        distanceKm: null
      };
    });
  }, [effectiveApplications]);

  const outcodeCentroids = useMemo(() => {
    const accum = new Map<string, { lat: number; lng: number; count: number }>();
    preparedApplications.forEach((item) => {
      if (!item.outcode || !isValidLatLng(item.lat, item.lng)) return;
      const existing = accum.get(item.outcode) ?? { lat: 0, lng: 0, count: 0 };
      existing.lat += item.lat as number;
      existing.lng += item.lng as number;
      existing.count += 1;
      accum.set(item.outcode, existing);
    });
    const centroids = new Map<string, { lat: number; lng: number }>();
    accum.forEach((value, key) => {
      if (value.count > 0) {
        centroids.set(key, {
          lat: value.lat / value.count,
          lng: value.lng / value.count
        });
      }
    });
    return centroids;
  }, [preparedApplications]);

  const combinedTextTerms = useMemo(() => {
    const locationLabel = location?.label ? normalizeText(location.label) : '';
    const locationTokens = locationLabel ? new Set(locationLabel.split(' ').filter(Boolean)) : null;

    const normalizedTokens = searchTokens
      .map(token => token.trim())
      .filter(Boolean)
      .map(token => normalizeText(token))
      .filter(term => {
        if (!term) return false;
        if (!locationTokens || !locationTokens.size) return true;
        return !locationTokens.has(term);
      });

    const normalizedInput = normalizeText(settledSearchInput);
    const terms = new Set<string>();
    normalizedTokens.forEach(term => { if (term) terms.add(term); });
    if (normalizedInput) {
      if (!locationTokens || !locationTokens.has(normalizedInput)) {
        terms.add(normalizedInput);
      }
    }
    return Array.from(terms);
  }, [searchTokens, settledSearchInput, location]);

  const textTermMatchers = useMemo(() => buildSearchTermMatchers(combinedTextTerms), [combinedTextTerms]);

  const committedQuery = useMemo(() => searchTokens.join(' ').trim(), [searchTokens]);
  const textFiltered = useMemo(() => {
    // The paged API has already applied q across the complete council dataset.
    // Reapplying a stricter client-side phrase matcher can discard valid server
    // results (for example, "student housing" matching student accommodation).
    if (hasRemoteSearchResultSet || textTermMatchers.length === 0) return preparedApplications;
    return preparedApplications.filter(item => {
      if (!item.normalizedText) return false;
      const addressRefText = [
        item.normalizedAddress,
        item.reference ? normalizeText(item.reference) : ''
      ].filter(Boolean).join(' ');
      const fullText = item.normalizedText;

      return textTermMatchers.every(matcher => {
        const target = matcher.addressish ? (addressRefText || fullText) : fullText;
        return matcher.pattern.test(target);
      });
    });
  }, [hasRemoteSearchResultSet, preparedApplications, textTermMatchers]);

  const exactLocationFiltered = useMemo<FilteredApplication[]>(() => (
    filterApplicationsByLocationRadius(textFiltered, location, 0)
  ), [textFiltered, location]);

  const locationFiltered = useMemo<FilteredApplication[]>(() => (
    hasRemoteSearchResultSet
      ? textFiltered
      : radiusKm === 0
      ? exactLocationFiltered
      : filterApplicationsByLocationRadius(textFiltered, location, radiusKm)
  ), [exactLocationFiltered, hasRemoteSearchResultSet, textFiltered, location, radiusKm]);

  const localFacetCounts = useMemo<FacetCounts>(() => {
    const categoryMap = new Map<string, number>();
    const tagMap = new Map<string, number>();
    const statusMap = new Map<string, number>();
    const postcodeMap = new Map<string, number>();
    let withDocuments = 0;
    let withoutDocuments = 0;
    let withForms = 0;
    let withoutForms = 0;

    locationFiltered.forEach(item => {
      if (item.category) {
        categoryMap.set(item.category, (categoryMap.get(item.category) ?? 0) + 1);
      }
      item.tags.forEach((tag) => {
        tagMap.set(tag, (tagMap.get(tag) ?? 0) + 1);
      });
      if (item.status) {
        statusMap.set(item.status, (statusMap.get(item.status) ?? 0) + 1);
      }
      if (item.outcode) {
        postcodeMap.set(item.outcode, (postcodeMap.get(item.outcode) ?? 0) + 1);
      }
      if (item.hasDocuments) {
        withDocuments += 1;
      } else {
        withoutDocuments += 1;
      }
      if (item.hasForms) {
        withForms += 1;
      } else {
        withoutForms += 1;
      }
    });

    const toSortedArray = (map: Map<string, number>) => Array.from(map.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));

    return {
      categories: toSortedArray(categoryMap),
      tags: toSortedArray(tagMap).map((item) => ({
        ...item,
        label: formatPlanningTagLabel(item.value)
      })),
      statuses: toSortedArray(statusMap),
      postcodePrefixes: toSortedArray(postcodeMap),
      documents: { withDocuments, withoutDocuments },
      forms: { withForms, withoutForms }
    };
  }, [locationFiltered]);

  const facetCounts = useMemo<FacetCounts>(() => {
    if (!hasRemoteSearchResultSet) return localFacetCounts;
    const facets = remoteSearchState.facets;
    const normalizeItems = (items: unknown): Array<{ value: string; count: number }> => (
      Array.isArray(items)
        ? items.flatMap((item) => {
            if (!item || typeof item !== 'object') return [];
            const value = String((item as { value?: unknown }).value || '').trim();
            const count = Number((item as { count?: unknown }).count);
            return value && Number.isFinite(count) ? [{ value, count }] : [];
          })
        : []
    );
    const documents = facets.documents;
    const forms = facets.forms;

    return {
      categories: normalizeItems(facets.categories),
      tags: normalizeItems(facets.tags).map((item) => ({
        ...item,
        label: formatPlanningTagLabel(item.value),
      })),
      statuses: normalizeItems(facets.statuses),
      postcodePrefixes: normalizeItems(facets.postcodePrefixes),
      documents: {
        withDocuments: Number(documents?.withDocuments) || 0,
        withoutDocuments: Number(documents?.withoutDocuments) || 0,
      },
      forms: {
        withForms: Number(forms?.withForms) || 0,
        withoutForms: Number(forms?.withoutForms) || 0,
      },
    };
  }, [hasRemoteSearchResultSet, localFacetCounts, remoteSearchState.facets]);

  const { start: dateStart, end: dateEnd } = useMemo(() => (
    getDateRange(facetState.datePreset, facetState.dateFrom, facetState.dateTo)
  ), [facetState.datePreset, facetState.dateFrom, facetState.dateTo]);

  const fullyFiltered = useMemo<FilteredApplication[]>(() => (
    hasRemoteSearchResultSet
      ? locationFiltered
      : filterApplicationsByFacets(locationFiltered, facetState, dateStart, dateEnd)
  ), [hasRemoteSearchResultSet, locationFiltered, facetState, dateStart, dateEnd]);

  const exactFullyFiltered = useMemo<FilteredApplication[]>(() => (
    hasRemoteSearchResultSet
      ? exactLocationFiltered
      : filterApplicationsByFacets(exactLocationFiltered, facetState, dateStart, dateEnd)
  ), [exactLocationFiltered, hasRemoteSearchResultSet, facetState, dateStart, dateEnd]);

  const sortedResultsWithMeta = useMemo<FilteredApplication[]>(() => {
    const sorted = [...fullyFiltered];
    sorted.sort((a, b) => {
      const dateA = getMostRelevantDateMs(a) ?? 0;
      const dateB = getMostRelevantDateMs(b) ?? 0;
      if (dateA === dateB) {
        return (b.distanceKm ?? Number.POSITIVE_INFINITY) - (a.distanceKm ?? Number.POSITIVE_INFINITY);
      }
      return dateB - dateA;
    });
    return sorted;
  }, [fullyFiltered]);

  const results = useMemo<PlanningSearchResult[]>(() => {
    return sortedResultsWithMeta.map(item => {
      const raw = item.raw as Record<string, unknown>;
      const referenceFromRaw = typeof raw.reference_no === 'string' ? raw.reference_no : null;
      const categoryFromRaw = typeof raw.category === 'string' ? raw.category : null;
      const statusFromRaw = typeof raw.status === 'string' ? raw.status : null;

      return {
        ...(raw as Record<string, unknown>),
        reference_no: referenceFromRaw ?? item.reference,
        category: categoryFromRaw ?? item.category,
        status: statusFromRaw ?? item.status,
        distanceKm: item.distanceKm
      } as PlanningSearchResult;
    });
  }, [sortedResultsWithMeta]);

  const resultCount = hasRemoteSearchResultSet ? remoteSearchState.total : results.length;
  const radiusSearchSummary = useMemo<RadiusSearchSummary>(() => {
    const hasLocation = Boolean(location && isValidLatLng(location.lat, location.lng));
    const exactLoadedCount = exactFullyFiltered.length;
    const exactResultCount = !hasLocation
      ? resultCount
      : radiusKm === 0
        ? resultCount
        : (hasMoreRemoteSearchResults && exactLoadedCount === 0 ? 1 : exactLoadedCount);
    const radiusResultCount = resultCount;

    return {
      hasLocation,
      locationLabel: location?.label || null,
      radiusKm,
      radiusLabel: formatRadiusLabel(radiusKm),
      exactResultCount,
      radiusResultCount,
      exactHasNoResultsButRadiusHasResults: Boolean(
        hasLocation &&
        radiusKm > 0 &&
        exactResultCount === 0 &&
        radiusResultCount > 0
      )
    };
  }, [exactFullyFiltered.length, hasMoreRemoteSearchResults, location, radiusKm, resultCount]);

  const getAnalyticsSnapshot = useCallback(() => {
    const activeFilterCount = countActivePlanningSearchControls(
      searchTokens,
      location,
      radiusKm,
      facetState
    );

    return {
      result_count: resultCount,
      result_count_bucket: countBucket(resultCount),
      active_filter_count: activeFilterCount,
      active_filter_bucket: countBucket(activeFilterCount),
      has_query: searchTokens.length > 0 || searchInput.trim().length > 0,
      search_token_count: searchTokens.length,
      has_location: Boolean(location),
      location_kind: location?.kind || 'none',
      radius_bucket: radiusBucket(radiusKm),
      category_count: facetState.categories.length,
      tag_count: facetState.tags.length,
      status_count: facetState.statuses.length,
      postcode_count: facetState.postcodePrefixes.length,
      has_date_filter: facetState.datePreset !== 'all' || Boolean(facetState.dateFrom || facetState.dateTo),
      has_impact_filter: facetState.impactMin !== null || facetState.impactMax !== null,
      documents_filter: facetState.hasDocuments === null ? 'any' : (facetState.hasDocuments ? 'with_documents' : 'without_documents'),
      forms_filter: facetState.hasForms === null ? 'any' : (facetState.hasForms ? 'with_forms' : 'without_forms')
    };
  }, [
    facetState,
    location,
    radiusKm,
    resultCount,
    searchInput,
    searchTokens
  ]);

  const trackSearchPageEvent = useCallback((
    action: string,
    context: PlanningUpdatesEventContext = {}
  ) => {
    trackPlanningUpdatesEvent(action, {
      ...context,
      metadata: {
        ...getAnalyticsSnapshot(),
        ...(context.metadata || {})
      }
    });
  }, [getAnalyticsSnapshot]);

  const applyRegionSearchIntent = useCallback((
    intent: Pick<PlanningSearchIntent, 'region' | 'originalQuery'>,
    trigger: 'search_input' | 'url_query' | 'search_suggestions' = 'search_input'
  ) => {
    const area = intent.region;
    if (!area || typeof window === 'undefined') return false;

    const currentPath = window.location.pathname || '';
    const surface = currentPath.startsWith('/planning-map') ? 'map' : 'updates';
    const href = buildPlanningSearchAreaPath(surface, area);
    const targetCouncilSlug = area.kind === 'country'
      ? AGGREGATE_COUNCIL_SLUG
      : (area.councilSlug || area.id);

    if (pendingQueryUpdateRef.current) {
      window.clearTimeout(pendingQueryUpdateRef.current);
      pendingQueryUpdateRef.current = null;
    }

    trackSearchPageEvent('planning_updates_search_region_resolved', {
      placement: trigger,
      entityType: 'planning_region',
      entityId: area.id,
      status: area.kind,
      metadata: {
        query: intent.originalQuery,
        area_id: area.id,
        area_kind: area.kind,
        target_council: targetCouncilSlug,
        href
      }
    });

    if (typeof switchCouncil === 'function') {
      switchCouncil(targetCouncilSlug);
    }

    const currentUrl = `${window.location.pathname}${window.location.search || ''}`;
    if (currentUrl !== href) {
      window.history.pushState(
        { ...(window.history.state || {}), as: href, url: href },
        '',
        href
      );
    }

    window.dispatchEvent(new CustomEvent(PLANNING_REGION_ROUTE_EVENT, {
      detail: {
        href,
        region: area.id,
        council: targetCouncilSlug,
        areaId: area.kind === 'country' || area.kind === 'council' ? null : area.id
      }
    }));

    return true;
  }, [switchCouncil, trackSearchPageEvent]);

  useEffect(() => {
    if (!hydratedFromQuery) return;
    if (typeof window === 'undefined') return;

    const snapshot = getAnalyticsSnapshot();
    const signature = [
      snapshot.result_count_bucket,
      snapshot.active_filter_count,
      snapshot.search_token_count,
      snapshot.location_kind,
      snapshot.radius_bucket,
      snapshot.category_count,
      snapshot.tag_count,
      snapshot.status_count,
      snapshot.postcode_count,
      snapshot.documents_filter,
      snapshot.forms_filter,
      snapshot.has_date_filter,
      snapshot.has_impact_filter
    ].join('|');

    if (signature === lastResultsAnalyticsSignatureRef.current) return;

    const handle = window.setTimeout(() => {
      lastResultsAnalyticsSignatureRef.current = signature;
      trackPlanningUpdatesEvent('planning_updates_results_viewed', {
        placement: 'results',
        status: String(snapshot.result_count_bucket),
        value: resultCount,
        metadata: snapshot
      });
    }, 650);

    return () => window.clearTimeout(handle);
  }, [getAnalyticsSnapshot, hydratedFromQuery, resultCount]);

  const removeToken = useCallback((value: string) => {
    trackSearchPageEvent('planning_updates_search_token_removed', {
      placement: 'search_chips',
      status: textLengthBucket(value),
      metadata: {
        token_length_bucket: textLengthBucket(value)
      }
    });
    setSearchTokens(prev => prev.filter(token => token !== value));
  }, [trackSearchPageEvent]);

  const clearTokens = useCallback(() => {
    if (searchTokens.length > 0) {
      trackSearchPageEvent('planning_updates_search_tokens_cleared', {
        placement: 'search_chips',
        status: countBucket(searchTokens.length),
        metadata: {
          cleared_token_count: searchTokens.length
        }
      });
    }
    setSearchTokens([]);
  }, [searchTokens.length, trackSearchPageEvent]);

  const includeShortLetsCategory = useCallback(() => {
    setCategoryFilterMode?.('include');
    setSelectedCategoryFilter?.(SHORT_LETS_CATEGORY_ID);
    setMultipleCategoryFilters?.((prev: string[]) => {
      const previous = Array.isArray(prev) ? prev : [];
      return previous.includes(SHORT_LETS_CATEGORY_ID)
        ? previous
        : [...previous, SHORT_LETS_CATEGORY_ID];
    });
    setFacetState(prev => {
      if (prev.categories.includes(SHORT_LETS_CATEGORY_ID)) return prev;
      return {
        ...prev,
        categories: [...prev.categories, SHORT_LETS_CATEGORY_ID]
      };
    });
  }, [setCategoryFilterMode, setMultipleCategoryFilters, setSelectedCategoryFilter]);

  const applyDeterministicSearchIntent = useCallback((value: string): PreparedPlanningSearchQuery => {
    const intent = parsePlanningSearchIntent(value);
    const shouldApplyRegion = shouldApplyPlanningSearchRegionDirectly(intent);
    const regionApplied = shouldApplyRegion
      ? applyRegionSearchIntent(intent, 'search_input')
      : false;
    if (intent.includesShortLets || isShortLetsSearchQuery(value)) {
      includeShortLetsCategory();
    }
    return {
      originalQuery: intent.originalQuery,
      residualQuery: intent.region && !regionApplied ? intent.originalQuery : intent.residualQuery,
      regionApplied,
      shortLetsApplied: intent.includesShortLets || isShortLetsSearchQuery(value),
    };
  }, [applyRegionSearchIntent, includeShortLetsCategory]);

  const addToken = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const intent = parsePlanningSearchIntent(trimmed);
    const tokenToAdd = intent.recognized
      ? intent.residualTokens[0] || ''
      : trimmed;

    if (intent.region) {
      applyRegionSearchIntent(intent, 'search_input');
    }
    if (intent.includesShortLets || isShortLetsSearchQuery(trimmed)) {
      includeShortLetsCategory();
    }

    if (!tokenToAdd) {
      updateSearchInput('');
      return;
    }

    if (!searchTokens.includes(tokenToAdd)) {
      const nextTokenCount = Math.min(searchTokens.length + 1, 12);
      trackSearchPageEvent('search_submitted', {
        placement: 'search_input',
        entityType: 'search_token',
        entityId: tokenToAdd,
        status: textLengthBucket(tokenToAdd),
        metadata: {
          token_length_bucket: textLengthBucket(tokenToAdd),
          original_query: trimmed,
          next_token_count: nextTokenCount
        }
      });
      trackSearchPageEvent('planning_updates_search_term_submitted', {
        placement: 'search_input',
        entityType: 'search_token',
        entityId: tokenToAdd,
        status: textLengthBucket(tokenToAdd),
        metadata: {
          token_length_bucket: textLengthBucket(tokenToAdd),
          original_query: trimmed,
          next_token_count: nextTokenCount
        }
      });
      trackSearchPageEvent('planning_updates_search_token_added', {
        placement: 'search_input',
        entityType: 'search_token',
        entityId: tokenToAdd,
        status: countBucket(nextTokenCount),
        metadata: {
          token_length_bucket: textLengthBucket(tokenToAdd),
          original_query: trimmed,
          next_token_count: nextTokenCount
        }
      });
    }
    setSearchTokens(prev => {
      if (prev.includes(tokenToAdd)) return prev;
      return [...prev, tokenToAdd].slice(0, 12);
    });
    updateSearchInput('');
  }, [applyRegionSearchIntent, includeShortLetsCategory, searchTokens, trackSearchPageEvent, updateSearchInput]);

  const setRadius = useCallback((value: number) => {
    const nextRadius = clampRadius(value);
    if (nextRadius !== radiusKm) {
      trackSearchPageEvent('planning_updates_radius_changed', {
        placement: 'location_radius',
        status: radiusBucket(nextRadius),
        value: nextRadius,
        metadata: {
          previous_radius_bucket: radiusBucket(radiusKm),
          next_radius_bucket: radiusBucket(nextRadius)
        }
      });
    }
    setRadiusKm(prev => {
      return nextRadius === prev ? prev : nextRadius;
    });
  }, [radiusKm, trackSearchPageEvent]);

  const toggleMapPreview = useCallback(() => {
    trackSearchPageEvent('planning_updates_map_preview_toggled', {
      placement: 'search_controls',
      status: mapPreview ? 'closed' : 'opened'
    });
    setMapPreview(prev => !prev);
  }, [mapPreview, trackSearchPageEvent]);

  const setLocation = useCallback((value: LocationCreationInput | LocationFilterState | null) => {
    if (value === null) {
      if (location) {
        trackSearchPageEvent('planning_updates_location_cleared', {
          placement: 'location_filter',
          status: location.kind,
          metadata: {
            previous_location_kind: location.kind,
            previous_location_source: location.source
          }
        });
      }
      setLocationState(null);
      return;
    }
    if ('kind' in value) {
      const normalized = normalizeLocationState(value);
      if (!normalized) {
        setLocationState(null);
        return;
      }
      trackSearchPageEvent('planning_updates_location_set', {
        placement: 'location_filter',
        status: normalized.kind,
        metadata: {
          location_kind: normalized.kind,
          location_source: normalized.source,
          location_accuracy: normalized.accuracy,
          has_house_number: Boolean(normalized.houseNumber)
        }
      });
      setLocationState(normalized);
      return;
    }
    const nextLocation = createLocationState(value);
    trackSearchPageEvent('planning_updates_location_set', {
      placement: 'location_filter',
      status: nextLocation.kind,
      metadata: {
        location_kind: nextLocation.kind,
        location_source: nextLocation.source,
        location_accuracy: nextLocation.accuracy,
        has_house_number: Boolean(nextLocation.houseNumber)
      }
    });
    setLocationState(nextLocation);
  }, [location, trackSearchPageEvent]);

  const toggleCategory = useCallback((value: string, forceInclude?: boolean) => {
    const exists = facetState.categories.includes(value);
    const shouldInclude = forceInclude !== undefined ? forceInclude : !exists;
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'category_filter',
      status: 'category',
      reason: shouldInclude ? 'enabled' : 'disabled',
      metadata: {
        filter_type: 'category',
        filter_state: shouldInclude ? 'enabled' : 'disabled',
        next_category_count: shouldInclude
          ? facetState.categories.length + (exists ? 0 : 1)
          : Math.max(0, facetState.categories.length - (exists ? 1 : 0))
      }
    });
    setFacetState(prev => {
      const prevExists = prev.categories.includes(value);
      const nextShouldInclude = forceInclude !== undefined ? forceInclude : !prevExists;
      return {
        ...prev,
        categories: nextShouldInclude
          ? [...prev.categories, value]
          : prev.categories.filter(cat => cat !== value)
      };
    });
  }, [facetState.categories, trackSearchPageEvent]);

  const setTags = useCallback((values: string[]) => {
    const tags = normalizePlanningTags(values);
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'tag_filter',
      status: 'tags',
      reason: tags.length > 0 ? 'enabled' : 'cleared',
      metadata: {
        filter_type: 'tags',
        tag_count: tags.length,
        tag_count_bucket: countBucket(tags.length)
      }
    });
    trackSearchPageEvent('planning_updates_tags_set', {
      placement: 'tag_filter',
      status: countBucket(tags.length),
      metadata: {
        tag_count: tags.length,
        tag_count_bucket: countBucket(tags.length)
      }
    });
    setFacetState(prev => ({
      ...prev,
      tags
    }));
  }, [trackSearchPageEvent]);

  const clearTags = useCallback(() => {
    if (facetState.tags.length > 0) {
      trackSearchPageEvent('planning_updates_tags_cleared', {
        placement: 'tag_filter',
        status: countBucket(facetState.tags.length),
        metadata: {
          cleared_tag_count: facetState.tags.length
        }
      });
    }
    setFacetState(prev => ({
      ...prev,
      tags: []
    }));
  }, [facetState.tags.length, trackSearchPageEvent]);

  const toggleTag = useCallback((value: string, forceInclude?: boolean) => {
    const tag = normalizePlanningTags([value])[0];
    if (!tag) return;
    const exists = facetState.tags.includes(tag);
    const shouldInclude = forceInclude !== undefined ? forceInclude : !exists;
    const nextTagCount = shouldInclude
      ? facetState.tags.length + (exists ? 0 : 1)
      : Math.max(0, facetState.tags.length - (exists ? 1 : 0));
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'tag_filter',
      status: 'tag',
      reason: shouldInclude ? 'enabled' : 'disabled',
      metadata: {
        filter_type: 'tag',
        filter_state: shouldInclude ? 'enabled' : 'disabled',
        next_tag_count: nextTagCount
      }
    });
    trackSearchPageEvent('planning_updates_tag_filter_toggled', {
      placement: 'tag_filter',
      status: shouldInclude ? 'selected' : 'removed',
      entityType: 'planning_tag',
      entityId: tag,
      metadata: {
        filter_state: shouldInclude ? 'enabled' : 'disabled',
        previous_tag_count: facetState.tags.length,
        next_tag_count: nextTagCount,
        next_tag_count_bucket: countBucket(nextTagCount)
      }
    });
    setFacetState(prev => {
      const exists = prev.tags.includes(tag);
      const shouldInclude = forceInclude !== undefined ? forceInclude : !exists;
      return {
        ...prev,
        tags: shouldInclude
          ? Array.from(new Set([...prev.tags, tag]))
          : prev.tags.filter(current => current !== tag)
      };
    });
  }, [facetState.tags, trackSearchPageEvent]);

  const toggleStatus = useCallback((value: string, forceInclude?: boolean) => {
    const exists = facetState.statuses.includes(value);
    const shouldInclude = forceInclude !== undefined ? forceInclude : !exists;
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'status_filter',
      status: 'status',
      reason: shouldInclude ? 'enabled' : 'disabled',
      metadata: {
        filter_type: 'status',
        filter_state: shouldInclude ? 'enabled' : 'disabled',
        next_status_count: shouldInclude
          ? facetState.statuses.length + (exists ? 0 : 1)
          : Math.max(0, facetState.statuses.length - (exists ? 1 : 0))
      }
    });
    setFacetState(prev => {
      const prevExists = prev.statuses.includes(value);
      const nextShouldInclude = forceInclude !== undefined ? forceInclude : !prevExists;
      return {
        ...prev,
        statuses: nextShouldInclude
          ? [...prev.statuses, value]
          : prev.statuses.filter(status => status !== value)
      };
    });
  }, [facetState.statuses, trackSearchPageEvent]);

  const setDatePreset = useCallback((preset: DatePreset) => {
    if (preset !== facetState.datePreset) {
      trackSearchPageEvent('planning_updates_filter_changed', {
        placement: 'date_filter',
        status: preset,
        reason: preset === 'all' ? 'cleared' : 'enabled',
        metadata: {
          filter_type: 'date_preset',
          previous_date_preset: facetState.datePreset,
          next_date_preset: preset
        }
      });
    }
    setFacetState(prev => ({
      ...prev,
      datePreset: preset,
      dateFrom: preset === 'custom' ? prev.dateFrom : null,
      dateTo: preset === 'custom' ? prev.dateTo : null
    }));
  }, [facetState.datePreset, trackSearchPageEvent]);

  const setCustomDateRange = useCallback((from: string | null, to: string | null) => {
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'date_filter',
      status: 'custom',
      reason: from || to ? 'enabled' : 'cleared',
      metadata: {
        filter_type: 'custom_date',
        has_from_date: Boolean(from),
        has_to_date: Boolean(to)
      }
    });
    setFacetState(prev => ({
      ...prev,
      datePreset: 'custom',
      dateFrom: from,
      dateTo: to
    }));
  }, [trackSearchPageEvent]);

  const setMapStatusFilter = useCallback((value: string) => {
    if (value !== mapFilters.status) {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'status',
        reason: value === 'All' ? 'cleared' : 'enabled',
        metadata: {
          filter_type: 'map_status',
          previous_map_status: mapFilters.status,
          next_map_status: value
        }
      });
    }
    setMapFilters(prev => (prev.status === value ? prev : { ...prev, status: value }));
  }, [mapFilters.status, trackSearchPageEvent]);

  const setMapScaleFilter = useCallback((value: string) => {
    if (value !== mapFilters.scale) {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'scale',
        reason: value === 'All' ? 'cleared' : 'enabled',
        metadata: {
          filter_type: 'map_scale',
          previous_map_scale: mapFilters.scale,
          next_map_scale: value
        }
      });
    }
    setMapFilters(prev => {
      const next = prev.scale === value ? prev : { ...prev, scale: value };
      const normalized = typeof value === 'string' ? value.trim() : '';
      if (!normalized || normalized.toLowerCase() === 'all') {
        return next.scaleMin === 1 && next.scaleMax === 10
          ? next
          : { ...next, scaleMin: 1, scaleMax: 10 };
      }
      if (normalized === '5+') {
        return next.scaleMin === 5 && next.scaleMax === 10
          ? next
          : { ...next, scaleMin: 5, scaleMax: 10 };
      }
      const numeric = Number(normalized);
      if (Number.isFinite(numeric)) {
        const clamped = Math.max(1, Math.min(10, numeric));
        return next.scaleMin === clamped && next.scaleMax === clamped
          ? next
          : { ...next, scaleMin: clamped, scaleMax: clamped };
      }
      return next;
    });
  }, [mapFilters.scale, trackSearchPageEvent]);

  const setMapScaleRange = useCallback((min: number, max: number) => {
    const nextMin = Number.isFinite(min) ? Math.max(1, Math.min(10, min)) : 1;
    const nextMax = Number.isFinite(max) ? Math.max(1, Math.min(10, max)) : 10;
    const normalizedMin = Math.min(nextMin, nextMax);
    const normalizedMax = Math.max(nextMin, nextMax);
    if (normalizedMin !== mapFilters.scaleMin || normalizedMax !== mapFilters.scaleMax || mapFilters.scale !== 'All') {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'scale_range',
        reason: normalizedMin === 1 && normalizedMax === 10 ? 'cleared' : 'enabled',
        metadata: {
          filter_type: 'map_scale_range',
          min_scale: normalizedMin,
          max_scale: normalizedMax
        }
      });
    }
    setMapFilters(prev => (
      prev.scaleMin === normalizedMin && prev.scaleMax === normalizedMax && prev.scale === 'All'
        ? prev
        : {
            ...prev,
            scaleMin: normalizedMin,
            scaleMax: normalizedMax,
            scale: 'All'
          }
    ));
  }, [mapFilters.scale, mapFilters.scaleMax, mapFilters.scaleMin, trackSearchPageEvent]);

  const setMapCategoryFilter = useCallback((value: string) => {
    if (value !== mapFilters.category) {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'category',
        reason: value === 'All' ? 'cleared' : 'enabled',
        metadata: {
          filter_type: 'map_category',
          previous_map_category: mapFilters.category,
          next_map_category: value
        }
      });
    }
    setMapFilters(prev => (prev.category === value ? prev : { ...prev, category: value }));
  }, [mapFilters.category, trackSearchPageEvent]);

  const setMapDateFilter = useCallback((value: string) => {
    if (value !== mapFilters.dateFilter) {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'date',
        reason: value === 'all' ? 'cleared' : 'enabled',
        metadata: {
          filter_type: 'map_date',
          previous_map_date_filter: mapFilters.dateFilter,
          next_map_date_filter: value
        }
      });
    }
    setMapFilters(prev => (prev.dateFilter === value ? prev : { ...prev, dateFilter: value }));
  }, [mapFilters.dateFilter, trackSearchPageEvent]);

  const setMapCustomDateRange = useCallback((from: string, to: string) => {
    trackSearchPageEvent('planning_updates_map_filter_changed', {
      placement: 'map_filters',
      status: 'custom_date',
      reason: from || to ? 'enabled' : 'cleared',
      metadata: {
        filter_type: 'map_custom_date',
        has_from_date: Boolean(from),
        has_to_date: Boolean(to)
      }
    });
    setMapFilters(prev => (
      prev.dateFrom === from && prev.dateTo === to
        ? prev
        : {
            ...prev,
            dateFrom: from,
            dateTo: to
          }
    ));
  }, [trackSearchPageEvent]);

  const setMapIncludeWithdrawns = useCallback((value: boolean) => {
    if (value !== mapFilters.includeWithdrawns) {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'include_withdrawns',
        reason: value ? 'enabled' : 'disabled',
        metadata: {
          filter_type: 'map_include_withdrawns',
          enabled: value
        }
      });
    }
    setMapFilters(prev => (prev.includeWithdrawns === value ? prev : { ...prev, includeWithdrawns: value }));
  }, [mapFilters.includeWithdrawns, trackSearchPageEvent]);

  const setMapShowOnlyFavorites = useCallback((value: boolean) => {
    if (value !== mapFilters.showOnlyFavorites) {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'favorites',
        reason: value ? 'enabled' : 'disabled',
        metadata: {
          filter_type: 'map_favorites',
          enabled: value
        }
      });
    }
    setMapFilters(prev => (prev.showOnlyFavorites === value ? prev : { ...prev, showOnlyFavorites: value }));
  }, [mapFilters.showOnlyFavorites, trackSearchPageEvent]);

  const setMapShowOnlyWorthWatching = useCallback((value: boolean) => {
    if (value !== mapFilters.showOnlyWorthWatching) {
      trackSearchPageEvent('planning_updates_map_filter_changed', {
        placement: 'map_filters',
        status: 'worth_watching',
        reason: value ? 'enabled' : 'disabled',
        metadata: {
          filter_type: 'map_worth_watching',
          enabled: value
        }
      });
    }
    setMapFilters(prev => (prev.showOnlyWorthWatching === value ? prev : { ...prev, showOnlyWorthWatching: value }));
  }, [mapFilters.showOnlyWorthWatching, trackSearchPageEvent]);

  const resetMapFilters = useCallback(() => {
    trackSearchPageEvent('planning_updates_map_filters_cleared', {
      placement: 'map_filters',
      status: 'cleared'
    });
    setMapFilters({ ...DEFAULT_MAP_FILTERS });
  }, [trackSearchPageEvent]);

  const setImpactRange = useCallback((min: number | null, max: number | null) => {
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'impact_filter',
      status: 'impact',
      reason: min === null && max === null ? 'cleared' : 'enabled',
      metadata: {
        filter_type: 'impact',
        min_impact: min ?? 0,
        max_impact: max ?? 10
      }
    });
    setFacetState(prev => ({
      ...prev,
      impactMin: min,
      impactMax: max
    }));
  }, [trackSearchPageEvent]);

  const setHasDocuments = useCallback((value: boolean | null) => {
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'document_filter',
      status: value === null ? 'any' : (value ? 'with_documents' : 'without_documents'),
      reason: value === null ? 'cleared' : 'enabled',
      metadata: {
        filter_type: 'documents',
        document_filter_value: value === null ? 'any' : (value ? 'with_documents' : 'without_documents')
      }
    });
    setFacetState(prev => ({
      ...prev,
      hasDocuments: value
    }));
  }, [trackSearchPageEvent]);

  const setHasForms = useCallback((value: boolean | null) => {
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'form_filter',
      status: value === null ? 'any' : (value ? 'with_forms' : 'without_forms'),
      reason: value === null ? 'cleared' : 'enabled',
      metadata: {
        filter_type: 'forms',
        form_filter_value: value === null ? 'any' : (value ? 'with_forms' : 'without_forms')
      }
    });
    setFacetState(prev => ({
      ...prev,
      hasForms: value
    }));
  }, [trackSearchPageEvent]);

  const togglePostcode = useCallback((value: string, forceInclude?: boolean) => {
    const exists = facetState.postcodePrefixes.includes(value);
    const shouldInclude = forceInclude !== undefined ? forceInclude : !exists;
    trackSearchPageEvent('planning_updates_filter_changed', {
      placement: 'postcode_filter',
      status: 'postcode',
      reason: shouldInclude ? 'enabled' : 'disabled',
      metadata: {
        filter_type: 'postcode',
        filter_state: shouldInclude ? 'enabled' : 'disabled',
        next_postcode_count: shouldInclude
          ? facetState.postcodePrefixes.length + (exists ? 0 : 1)
          : Math.max(0, facetState.postcodePrefixes.length - (exists ? 1 : 0))
      }
    });
    setFacetState(prev => {
      const exists = prev.postcodePrefixes.includes(value);
      const shouldInclude = forceInclude !== undefined ? forceInclude : !exists;
      return {
        ...prev,
        postcodePrefixes: shouldInclude
          ? [...prev.postcodePrefixes, value]
          : prev.postcodePrefixes.filter(code => code !== value)
      };
    });
  }, [facetState.postcodePrefixes, trackSearchPageEvent]);

  const clearAll = useCallback(() => {
    trackSearchPageEvent('planning_updates_filters_cleared', {
      placement: 'filter_controls',
      status: 'cleared'
    });
    setSearchTokens([]);
    setSearchInput('');
    setLocationState(null);
    setRadiusKm(0);
    setFacetState(createEmptyFacetState());
    setMapPreview(false);
    setGeocodeError(null);
  }, [trackSearchPageEvent]);
  const activeChips = useMemo<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];

    searchTokens.forEach(token => {
      chips.push({
        id: `token-${token}`,
        label: token,
        category: 'token',
        onRemove: () => removeToken(token)
      });
    });

    if (location && isValidLatLng(location.lat, location.lng)) {
      chips.push({
        id: 'location',
        label: `Within ${formatRadiusLabel(radiusKm)} of ${location.label}`,
        category: 'location',
        onRemove: () => setLocation(null)
      });
    }

    facetState.categories.forEach(category => {
      chips.push({
        id: `category-${category}`,
        label: category,
        category: 'category',
        onRemove: () => toggleCategory(category, false)
      });
    });

    facetState.tags.forEach(tag => {
      chips.push({
        id: `tag-${tag}`,
        label: formatPlanningTagLabel(tag),
        category: 'tag',
        onRemove: () => toggleTag(tag, false)
      });
    });

    facetState.statuses.forEach(status => {
      chips.push({
        id: `status-${status}`,
        label: status,
        category: 'status',
        onRemove: () => toggleStatus(status, false)
      });
    });

    if (facetState.datePreset !== 'all' || facetState.dateFrom || facetState.dateTo) {
      chips.push({
        id: 'date',
        label: formatDateLabel(facetState.datePreset, facetState.dateFrom, facetState.dateTo),
        category: 'date',
        onRemove: () => setDatePreset('all')
      });
    }

    if (facetState.impactMin !== null || facetState.impactMax !== null) {
      chips.push({
        id: 'impact',
        label: `Impact ${facetState.impactMin ?? 0}-${facetState.impactMax ?? 10}`,
        category: 'impact',
        onRemove: () => setImpactRange(null, null)
      });
    }

      if (facetState.hasDocuments !== null) {
        chips.push({
          id: 'documents',
          label: facetState.hasDocuments ? 'Has documents' : 'No documents',
          category: 'documents',
          onRemove: () => setHasDocuments(null)
        });
      }

      if (facetState.hasForms !== null) {
        chips.push({
          id: 'forms',
          label: facetState.hasForms ? 'Has form data' : 'No form data',
          category: 'forms',
          onRemove: () => setHasForms(null)
        });
      }

    facetState.postcodePrefixes.forEach(prefix => {
      chips.push({
        id: `postcode-${prefix}`,
        label: `Area ${prefix}`,
        category: 'postcode',
        onRemove: () => togglePostcode(prefix, false)
      });
    });

    return chips;
    }, [searchTokens, location, radiusKm, facetState, removeToken, setLocation, toggleCategory, toggleTag, toggleStatus, setDatePreset, setImpactRange, setHasDocuments, setHasForms, togglePostcode]);
  useEffect(() => {
    if (!hydratedFromQuery) return;

    if (photonAbortControllerRef.current) {
      photonAbortControllerRef.current.abort();
      photonAbortControllerRef.current = null;
    }

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      const trimmed = searchInput.trim();
      const limit = 8;
      // References go straight to record search. A place geocoder interprets their
      // numeric segments as unrelated postcodes and distracts from that lookup.
      if (isPlanningReferenceQuery(trimmed)) {
        if (!cancelled) setSuggestions([]);
        return;
      }
      const baseSuggestions = buildTypeaheadSuggestions(
        baseApplications,
        searchInput,
        { limit: limit * 2 }
      );
      const regionSuggestions: SearchSuggestion[] = trimmed
        ? searchPlanningSearchRegions(trimmed, 4).map((area) => ({
          id: getPlanningSearchRegionSuggestionId(area),
          label: area.kind === 'country' ? 'All regions' : area.displayName,
          type: 'area' as const,
          subtitle: area.kind === 'country' ? 'Region' : 'Council region',
          score: 1.2
        }))
        : [];
      const dedupe = new Set<string>();
      const merged: SearchSuggestion[] = [];

      for (const suggestion of [...regionSuggestions, ...baseSuggestions]) {
        const key = `${suggestion.type}:${suggestion.label}`.toLowerCase();
        if (!dedupe.has(key)) {
          dedupe.add(key);
          merged.push(suggestion);
        }
      }

      if (trimmed.length >= 3) {
        const controller = new AbortController();
        photonAbortControllerRef.current = controller;
        try {
          const photonResults = await searchPhoton(trimmed, {
            signal: controller.signal,
            bias: { lat: defaultLocation.lat, lon: defaultLocation.lng },
            preferredTerms: [
              councilConfig?.displayName,
              councilConfig?.officialName
            ].filter((term): term is string => typeof term === 'string' && term.length > 0),
            postcodePrefixes: ['EH']
          });
          photonResults.forEach((feature, index) => {
            const label = formatPhotonLabel(feature);
            const lat = feature.geometry?.coordinates?.[1];
            const lng = feature.geometry?.coordinates?.[0];
            if (!label || lat == null || lng == null) return;
            const key = `address:${label}`.toLowerCase();
            if (dedupe.has(key)) return;
            dedupe.add(key);
            merged.push({
              id: `photon-${feature.properties?.osm_id ?? `${index}-${label}`}`,
              label,
              subtitle: feature.properties?.city ?? feature.properties?.state ?? feature.properties?.country ?? 'Address',
              type: 'address',
              lat,
              lng,
              score: 0.9
            });
          });
        } catch (error) {
          if ((error as { name?: string })?.name !== 'AbortError') {
            console.warn('Photon suggestion lookup failed', error);
          }
        } finally {
          if (photonAbortControllerRef.current === controller) {
            photonAbortControllerRef.current = null;
          }
        }
      }

      if (cancelled) return;

      if (!trimmed) {
        setSuggestions(merged.slice(0, limit));
        return;
      }

      const sortByScore = (items: SearchSuggestion[]) =>
        items.slice().sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

      const placeSuggestions = merged.filter(
        (s) => s.type === 'address' || s.type === 'postcode' || s.type === 'area'
      );
      const termSuggestions = merged.filter(
        (s) => s.type === 'keyword' || s.type === 'category'
      );

      const isStreetSuggestion = (s: SearchSuggestion) =>
        Boolean(s.subtitle && s.subtitle.toLowerCase() === 'street');
      const isKeywordSuggestion = (s: SearchSuggestion) =>
        s.type === 'keyword' ||
        (s.subtitle && (s.subtitle.toLowerCase().includes('keyword') || s.subtitle.toLowerCase().includes('term')));
      const promoteStreets = (items: SearchSuggestion[]) => {
        const streets = items.filter(isStreetSuggestion);
        const rest = items.filter(item => !isStreetSuggestion(item));
        return [...streets, ...rest];
      };
      const promoteKeywords = (items: SearchSuggestion[]) => {
        const keywords = items.filter(isKeywordSuggestion);
        const rest = items.filter(item => !isKeywordSuggestion(item));
        return [...keywords, ...rest];
      };

      const termTarget = Math.max(3, Math.round(limit * 0.4));
      const placeTarget = Math.max(1, limit - termTarget);

      const selectedPlaces = promoteStreets(sortByScore(placeSuggestions)).slice(0, placeTarget);
      const selectedTerms = sortByScore(termSuggestions).slice(0, termTarget);

      // Fill shortfall from the other bucket if one is sparse
      const remainingSlots = limit - (selectedPlaces.length + selectedTerms.length);
      const leftovers =
        selectedPlaces.length < placeTarget
          ? sortByScore(termSuggestions.filter(t => !selectedTerms.includes(t)))
          : sortByScore(placeSuggestions.filter(p => !selectedPlaces.includes(p)));
      const filler = leftovers.slice(0, Math.max(0, remainingSlots));

      // Put keyword/planning-term items first, then promote streets within the remainder.
      const prioritized = promoteKeywords(
        promoteStreets([...selectedTerms, ...selectedPlaces, ...filler])
      ).slice(0, limit);

      setSuggestions(prioritized);
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
      if (photonAbortControllerRef.current) {
        photonAbortControllerRef.current.abort();
        photonAbortControllerRef.current = null;
      }
    };
  }, [baseApplications, councilConfig, defaultLocation, searchInput, hydratedFromQuery]);
  const geocodeFromInput = useCallback((raw: string): LocationFilterState | null => {
    const query = raw.trim();
    if (!query) return null;
    const normalized = normalizeText(query);
    const variants = generateLocationVariants(normalized);
    const tokens = normalized.split(' ').filter(Boolean);
    let bestMatch: PreparedApplication | null = null;
    let bestScore = 0;

    for (let i = 0; i < preparedApplications.length && i < 5000; i += 1) {
      const item = preparedApplications[i];
      if (!item.normalizedAddress || !isValidLatLng(item.lat, item.lng)) continue;
      const address = item.normalizedAddress;
      const addressTokens = item.addressTokens;

      let candidateScore = 0;
      for (const variant of variants) {
        const idx = address.indexOf(variant);
        if (idx === -1) continue;
        const coverage = variant.length / address.length;
        const contiguousBonus = variant.split(' ').length >= tokens.length ? 0.3 : 0;
        const positionBoost = idx === 0 ? 0.2 : 0;
        candidateScore = Math.max(candidateScore, 0.6 + coverage + contiguousBonus + positionBoost);
      }

      if (candidateScore === 0 && tokens.length > 0) {
        const matchedTokens = tokens.filter(token => addressTokens.includes(token));
        const matchCount = matchedTokens.length;
        if (matchCount > 0) {
          const coverage = matchCount / tokens.length;
          const reverseCoverage = matchCount / (addressTokens.length || 1);
          const orderBonus = () => {
            let lastIndex = -1;
            for (const token of matchedTokens) {
              const idx = addressTokens.indexOf(token, lastIndex + 1);
              if (idx === -1) return 0;
              lastIndex = idx;
            }
            return 0.15;
          };
          candidateScore = Math.max(candidateScore, 0.45 + coverage * 0.6 + reverseCoverage * 0.3 + orderBonus());
        }
      }

      const similarity = diceCoefficient(normalized, address);
      if (similarity > 0) {
        candidateScore = Math.max(candidateScore, 0.5 + similarity * 0.6);
      }

      if (candidateScore > bestScore) {
        bestScore = candidateScore;
        bestMatch = item;
      }
    }

    if (bestMatch && bestScore >= DATASET_MATCH_THRESHOLD && isValidLatLng(bestMatch.lat, bestMatch.lng)) {
      const rawRecord = bestMatch.raw as Record<string, unknown>;
      const rawAddress = getRecordString(rawRecord, 'address');
      const rawLocation = getRecordString(rawRecord, 'location');
      const label = rawAddress ?? rawLocation ?? query;
      return createLocationState({
        label,
        lat: bestMatch.lat as number,
        lng: bestMatch.lng as number,
        source: 'dataset',
        accuracy: 'parcel',
        streetKey: bestMatch.streetKey,
        houseNumber: bestMatch.houseNumber
      });
    }

    const postcode = extractPostcode(query);
    if (postcode) {
      const postcodeMatch = preparedApplications.find(item => item.postcode === postcode && isValidLatLng(item.lat, item.lng));
      if (postcodeMatch && isValidLatLng(postcodeMatch.lat, postcodeMatch.lng)) {
        return createLocationState({
          label: postcode,
          lat: postcodeMatch.lat as number,
          lng: postcodeMatch.lng as number,
          source: 'postcode',
          accuracy: 'postcode'
        });
      }
      const postcodePrefix = postcode.split(' ')[0];
      const centroid = outcodeCentroids.get(postcodePrefix);
      if (centroid) {
        return createLocationState({
          label: postcode,
          lat: centroid.lat,
          lng: centroid.lng,
          source: 'postcode',
          accuracy: 'postcode'
        });
      }
    }

    const outcode = extractPostcodePrefix(query);
    if (outcode) {
      const centroid = outcodeCentroids.get(outcode);
      if (centroid) {
        return createLocationState({
          label: outcode,
          lat: centroid.lat,
          lng: centroid.lng,
          source: 'postcode',
          accuracy: 'postcode'
        });
      }
    }

    return createLocationState({
      label: query,
      lat: defaultLocation.lat,
      lng: defaultLocation.lng,
      source: 'fallback',
      accuracy: 'city'
    });
  }, [defaultLocation, preparedApplications, outcodeCentroids]);
  const requestGeocode = useCallback(async (value: string) => {
    trackSearchPageEvent('planning_updates_geocode_requested', {
      placement: 'location_search',
      status: textLengthBucket(value),
      metadata: {
        input_length_bucket: textLengthBucket(value)
      }
    });
    setPendingGeocode(true);
    setGeocodeError(null);
    try {
      await new Promise(resolve => setTimeout(resolve, 120));
      const result = geocodeFromInput(value);
      if (!result || !isValidLatLng(result.lat, result.lng)) {
        trackSearchPageEvent('planning_updates_geocode_failed', {
          placement: 'location_search',
          status: textLengthBucket(value),
          metadata: {
            input_length_bucket: textLengthBucket(value)
          }
        });
        setGeocodeError('Unable to locate that address. Try refining your search.');
      } else {
        const normalizedQuery = normalizeText(value);
        setLocation(isAddressishSearchTerm(normalizedQuery)
          ? result
          : {
              ...result,
              label: value.trim(),
              accuracy: 'city',
              streetKey: null,
              houseNumber: null
            });
      }
    } finally {
      setPendingGeocode(false);
    }
  }, [geocodeFromInput, setLocation, trackSearchPageEvent]);
  const selectSuggestion = useCallback((suggestion: SearchSuggestion) => {
    if (suggestion.streetKey) {
      streetTokenRef.current = suggestion.streetKey;
    }
    const typed = lastTypedRef.current.trim();
    const regionSuggestion = getPlanningSearchRegionFromSuggestionId(suggestion.id);
    trackSearchPageEvent('planning_updates_suggestion_selected', {
      placement: 'search_suggestions',
      status: suggestion.type,
      metadata: {
        suggestion_type: suggestion.type,
        input_length_bucket: textLengthBucket(typed),
        has_coordinates: suggestion.lat != null && suggestion.lng != null,
        has_street_key: Boolean(suggestion.streetKey)
      }
    });
    switch (suggestion.type) {
      case 'address':
        if (suggestion.lat != null && suggestion.lng != null) {
          setLocation({
            label: suggestion.label,
            lat: suggestion.lat,
            lng: suggestion.lng,
            source: 'dataset',
            accuracy: 'parcel'
          });
        } else {
          setLocation(geocodeFromInput(suggestion.label));
        }
        break;
      case 'postcode':
        setLocation(geocodeFromInput(suggestion.label));
        togglePostcode(suggestion.label.split(' ')[0], true);
        break;
      case 'area':
        if (regionSuggestion) {
          applyRegionSearchIntent(
            { region: regionSuggestion, originalQuery: typed || suggestion.label },
            'search_suggestions'
          );
          break;
        }
        togglePostcode(suggestion.label, true);
        break;
      case 'category':
        toggleCategory(suggestion.label, true);
        break;
      default:
        addToken(suggestion.label);
        break;
    }
    // Preserve the user's typed text as a search token so partial street text (e.g., "howe") stays active
    if (typed && !regionSuggestion) {
      addToken(typed);
    }
    updateSearchInput('');
    lastTypedRef.current = '';
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>('input[data-global-search-input="true"]');
        input?.blur();
      }, 0);
    }
  }, [addToken, applyRegionSearchIntent, geocodeFromInput, setLocation, toggleCategory, togglePostcode, trackSearchPageEvent, updateSearchInput]);
  useEffect(() => {
    const liveInput = searchInput.trim() || lastTypedRef.current;
    const baseQuery = committedQuery && committedQuery.trim().length > 0
      ? committedQuery
      : liveInput;
    const nextQuery = baseQuery ? baseQuery.trim() : '';
    if (committedQuery || !nextQuery) {
      setSearchQuery(nextQuery);
      return undefined;
    }

    const handle = window.setTimeout(() => {
      setSearchQuery(nextQuery);
    }, 160);

    return () => window.clearTimeout(handle);
  }, [committedQuery, searchInput, setSearchQuery]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(getSavedSearchStorageKey(councilSlug));
      if (!raw) {
        setSavedSearches([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setSavedSearches(parsed);
      }
    } catch (error) {
      console.warn('Failed to load saved searches', error);
    }
  }, [councilSlug]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(getSavedSearchStorageKey(councilSlug), JSON.stringify(savedSearches));
    } catch (error) {
      console.warn('Failed to persist saved searches', error);
    }
  }, [councilSlug, savedSearches, updateSearchInput]);
  const serializeCurrentSearch = useCallback((): SavedSearch => ({
    id: `${Date.now()}`,
    name: 'Unnamed search',
    createdAt: new Date().toISOString(),
    tokens: [...searchTokens],
    location: location ? { ...location } : null,
    radiusKm,
    facetState: {
      categories: [...facetState.categories],
      tags: [...facetState.tags],
      statuses: [...facetState.statuses],
      datePreset: facetState.datePreset,
      dateFrom: facetState.dateFrom,
      dateTo: facetState.dateTo,
      impactMin: facetState.impactMin,
      impactMax: facetState.impactMax,
      hasDocuments: facetState.hasDocuments,
      hasForms: facetState.hasForms,
      postcodePrefixes: [...facetState.postcodePrefixes]
    },
    mapPreview,
    alertsEnabled
  }), [searchTokens, location, radiusKm, facetState, mapPreview, alertsEnabled]);

  const buildCurrentAlertCriteria = useCallback((): PlanningAlertCriteria | null => {
    if (!location || !isValidLatLng(location.lat, location.lng)) {
      return null;
    }

    const criteria: PlanningAlertCriteria = {
      recordTypes: ['planning', 'short_term_let'],
      area: {
        type: 'radius',
        label: location.label,
        latitude: location.lat,
        longitude: location.lng,
        radiusKm: Math.min(radiusKm, 2)
      }
    };

    const query = searchTokens.join(' ').trim();
    if (query) criteria.query = query;
    if (facetState.categories.length > 0) criteria.categories = [...facetState.categories];
    if (facetState.tags.length > 0) criteria.tags = [...facetState.tags];
    if (facetState.statuses.length > 0) criteria.statuses = [...facetState.statuses];
    if (facetState.postcodePrefixes.length > 0) criteria.postcodePrefixes = [...facetState.postcodePrefixes];
    if (facetState.impactMin !== null) criteria.impactMin = facetState.impactMin;
    if (facetState.impactMax !== null) criteria.impactMax = facetState.impactMax;
    if (facetState.hasDocuments !== null) criteria.hasDocuments = facetState.hasDocuments;
    if (facetState.hasForms !== null) criteria.hasApplicationForm = facetState.hasForms;

    return criteria;
  }, [facetState, location, radiusKm, searchTokens]);

  const saveCurrentSearch = useCallback((name: string, options?: { enableAlerts?: boolean }) => {
    trackSearchPageEvent('save_clicked', {
      placement: 'saved_search',
      status: options?.enableAlerts ? 'alerts_enabled' : 'saved_only',
      metadata: {
        saved_search_name_length_bucket: textLengthBucket(name),
        enable_alerts: Boolean(options?.enableAlerts)
      }
    });
    trackSearchPageEvent('planning_updates_saved_search_created', {
      placement: 'saved_search',
      status: options?.enableAlerts ? 'alerts_enabled' : 'saved_only',
      metadata: {
        saved_search_name_length_bucket: textLengthBucket(name),
        enable_alerts: Boolean(options?.enableAlerts)
      }
    });
    setSavedSearches(prev => {
      const snapshot = serializeCurrentSearch();
      const record: SavedSearch = {
        ...snapshot,
        id: `${snapshot.id}-${prev.length + 1}`,
        name: name.trim() || 'Saved search',
        alertsEnabled: options?.enableAlerts ?? snapshot.alertsEnabled
      };
      return [record, ...prev].slice(0, 25);
    });
    if (options?.enableAlerts !== undefined) {
      setAlertsEnabled(options.enableAlerts);
    }
  }, [serializeCurrentSearch, trackSearchPageEvent]);

  const deleteSavedSearch = useCallback((id: string) => {
    trackSearchPageEvent('planning_updates_saved_search_deleted', {
      placement: 'saved_search',
      status: 'deleted'
    });
    setSavedSearches(prev => prev.filter(item => item.id !== id));
  }, [trackSearchPageEvent]);

  const applySavedSearch = useCallback((id: string) => {
    const saved = savedSearches.find(item => item.id === id);
    if (!saved) return;
    trackSearchPageEvent('planning_updates_saved_search_applied', {
      placement: 'saved_search',
      status: saved.alertsEnabled ? 'alerts_enabled' : 'saved_only',
      metadata: {
        saved_token_count: saved.tokens.length,
        saved_tag_count: saved.facetState.tags?.length ?? 0,
        saved_category_count: saved.facetState.categories.length,
        saved_status_count: saved.facetState.statuses.length,
        saved_has_location: Boolean(saved.location)
      }
    });
    setSearchTokens([...saved.tokens]);
    setLocationState(saved.location ? { ...saved.location } : null);
    setRadiusKm(saved.radiusKm);
    setFacetState({
      categories: [...saved.facetState.categories],
      tags: [...(saved.facetState.tags ?? [])],
      statuses: [...saved.facetState.statuses],
      datePreset: saved.facetState.datePreset,
      dateFrom: saved.facetState.dateFrom,
      dateTo: saved.facetState.dateTo,
      impactMin: saved.facetState.impactMin,
      impactMax: saved.facetState.impactMax,
      hasDocuments: saved.facetState.hasDocuments ?? null,
      hasForms: saved.facetState.hasForms ?? null,
      postcodePrefixes: [...saved.facetState.postcodePrefixes]
    });
    setMapPreview(saved.mapPreview);
    setAlertsEnabled(Boolean(saved.alertsEnabled));
    updateSearchInput('');
  }, [savedSearches, trackSearchPageEvent, updateSearchInput]);

  const updateAlertsEnabled = useCallback((value: boolean) => {
    if (value !== alertsEnabled) {
      trackSearchPageEvent('planning_updates_alert_intent_changed', {
        placement: 'results_toolbar',
        status: value ? 'enabled' : 'disabled',
        metadata: {
          enabled: value
        }
      });
    }
    setAlertsEnabled(value);
  }, [alertsEnabled, trackSearchPageEvent]);

  const buildQueryParamsFromState = useCallback(() => {
    const params: Record<string, string | null> = {};

    if (searchTokens.length > 0) {
      params.q = encodeListParam(searchTokens);
    } else {
      params.q = null;
    }

    if (location && isValidLatLng(location.lat, location.lng)) {
      params.loc = location.label;
      params.lat = location.lat.toFixed(6);
      params.lng = location.lng.toFixed(6);
    } else {
      params.loc = null;
      params.lat = null;
      params.lng = null;
    }

    params.radius = location || radiusKm !== 0 ? radiusKm.toString() : null;

    params.categories = encodeListParam(facetState.categories) ?? null;
    params.tags = encodeListParam(facetState.tags) ?? null;
    params.statuses = encodeListParam(facetState.statuses) ?? null;
    params.postcodes = encodeListParam(facetState.postcodePrefixes) ?? null;

    params.datePreset = facetState.datePreset !== 'all' ? facetState.datePreset : null;
    params.from = facetState.dateFrom;
    params.to = facetState.dateTo;

    if (facetState.impactMin !== null || facetState.impactMax !== null) {
      params.impact = `${facetState.impactMin ?? ''}-${facetState.impactMax ?? ''}`;
    } else {
      params.impact = null;
    }

    if (facetState.hasDocuments !== null) {
      params.docs = facetState.hasDocuments ? '1' : '0';
    } else {
      params.docs = null;
    }

    if (facetState.hasForms !== null) {
      params.forms = facetState.hasForms ? '1' : '0';
    } else {
      params.forms = null;
    }

    params.map = mapPreview ? '1' : null;

    return params;
  }, [searchTokens, location, radiusKm, facetState, mapPreview]);
  useEffect(() => {
    const pathname = getRuntimePathname(router.pathname);
    if (!pathname) return;
    if (!pathname.startsWith('/planning-updates') && !pathname.startsWith('/planning-map')) return;

    const runtimeUrl = typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : router.asPath;
    // Next's router query object can change identity during unrelated data
    // updates. Rehydrating the same URL in that case overwrites a filter the
    // user has just selected before the low-churn URL sync runs.
    if (hydratedFromQuery && runtimeUrl === lastHydratedRuntimeUrlRef.current) return;

    const params = getRuntimeSearchQuery(router.query);
    normalizeRuntimeSearchUrl();
    const signature = computeQuerySignature({
      q: ensureArrayValue(params.q ?? params.tokens ?? params.search),
      loc: ensureArrayValue(params.loc),
      lat: ensureArrayValue(params.lat),
      lng: ensureArrayValue(params.lng),
      radius: ensureArrayValue(params.radius),
      categories: ensureArrayValue(params.categories || params.category),
      tags: ensureArrayValue(params.tags),
      statuses: ensureArrayValue(params.statuses),
      datePreset: ensureArrayValue(params.datePreset),
      from: ensureArrayValue(params.from),
      to: ensureArrayValue(params.to),
      impact: ensureArrayValue(params.impact),
      docs: ensureArrayValue(params.docs),
      forms: ensureArrayValue(params.forms),
      postcodes: ensureArrayValue(params.postcodes),
      map: ensureArrayValue(params.map)
    });

    if (signature === lastQuerySignatureRef.current && hydratedFromQuery) {
      return;
    }

    lastQuerySignatureRef.current = signature;
    lastHydratedRuntimeUrlRef.current = runtimeUrl;

    const rawTokenList = parseListParam(params.q ?? params.tokens ?? params.search);
    const searchIntent = parsePlanningSearchIntent(rawTokenList);
    const tokenList = londonScope ? rawTokenList : searchIntent.residualTokens;
    if (!londonScope && searchIntent.region) {
      applyRegionSearchIntent(searchIntent, 'url_query');
    }
    if (!londonScope && searchIntent.includesShortLets) {
      includeShortLetsCategory();
    }
    const tokensSignature = tokenList.join('||');
    const tokensChanged = tokensSignature !== hydratedTokenSignatureRef.current;

    if (tokensChanged) {
      setSearchTokens(tokenList);
      hydratedTokenSignatureRef.current = tokensSignature;
    } else if (!hydratedTokenSignatureRef.current) {
      hydratedTokenSignatureRef.current = tokensSignature;
    }

    if (!hydratedFromQuery || tokensChanged) {
      setSearchInput('');
    }

    const radius = parseNumberParam(params.radius, 0);
    setRadiusKm(radius ?? 0);

    const lat = parseNumberParam(params.lat);
    const lng = parseNumberParam(params.lng);
    const locLabel = decodeQueryPart(ensureArrayValue(params.loc));

    if (lat !== null && lng !== null && isValidLatLng(lat, lng) && locLabel) {
      setLocationState(
        createLocationState({
          label: locLabel,
          lat,
          lng,
          source: 'manual',
          accuracy: 'city'
        })
      );
    } else {
      setLocationState(null);
    }

    const explicitCategories = parseListParam(params.categories || params.category);
    const categories = explicitCategories.length > 0
      ? explicitCategories
      : !londonScope && (searchIntent.includesShortLets || isShortLetsSearchQuery(rawTokenList))
        ? [SHORT_LETS_CATEGORY_ID]
        : [];
    const tags = normalizePlanningTags(parseListParam(params.tags));
    const statuses = parseListParam(params.statuses);
    const postcodePrefixes = parseListParam(params.postcodes);
    const datePreset = (ensureArrayValue(params.datePreset) as DatePreset) || 'all';
    const { min, max } = parseImpactParam(params.impact);
    const docs = parseBooleanParam(params.docs);
    const forms = parseBooleanParam(params.forms);
    const tagSignature = tags.join('||');
    if (tags.length > 0 && tagSignature !== hydratedTagSignatureRef.current) {
      trackPlanningUpdatesEvent('planning_updates_tag_url_hydrated', {
        placement: 'url_query',
        status: countBucket(tags.length),
        metadata: {
          tag_count: tags.length,
          tag_count_bucket: countBucket(tags.length),
          has_query: tokenList.length > 0,
          has_location: Boolean(locLabel && lat !== null && lng !== null),
          has_other_filters: categories.length > 0 || statuses.length > 0 || postcodePrefixes.length > 0
        }
      });
    }
    hydratedTagSignatureRef.current = tagSignature;

    setFacetState(prev => ({
      ...prev,
      categories,
      tags,
      statuses,
      postcodePrefixes,
      datePreset: ['7d','30d','90d','180d','year','all','custom'].includes(datePreset) ? datePreset : 'all',
      dateFrom: ensureArrayValue(params.from) || null,
      dateTo: ensureArrayValue(params.to) || null,
      impactMin: min,
      impactMax: max,
      hasDocuments: docs,
      hasForms: forms
    }));

    setMapPreview(ensureArrayValue(params.map) === '1');
    setHydratedFromQuery(true);
  }, [
    applyRegionSearchIntent,
    hydratedFromQuery,
    includeShortLetsCategory,
    londonScope,
    router.asPath,
    router.isReady,
    router.pathname,
    router.query
  ]);
  useEffect(() => {
    if (!URL_SYNC_ENABLED) return;
    if (!hydratedFromQuery) return;
    const pathname = getRuntimePathname(router.pathname);
    if (!pathname) return;
    if (!pathname.startsWith('/planning-updates') && !pathname.startsWith('/planning-map')) return;

    const params = buildQueryParamsFromState();
    const signature = computeQuerySignature(params);
    if (signature === lastQuerySignatureRef.current) return;

    const nextQuery = stripRouteQueryKeys(getRuntimeSearchQuery(router.query)) as Record<string, string | string[] | undefined>;
    SEARCH_QUERY_KEYS.forEach(key => {
      delete nextQuery[key];
    });
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      nextQuery[key] = value;
    });

    if (pendingQueryUpdateRef.current) {
      window.clearTimeout(pendingQueryUpdateRef.current);
    }

    pendingQueryUpdateRef.current = window.setTimeout(() => {
      lastQuerySignatureRef.current = signature;
      // Avoid triggering a full router re-render; update the URL directly.
      if (typeof window !== 'undefined') {
        if (window.location.pathname !== pathname) return;
        const nextUrl = patchPlanningQuery(window.location.href, params, SEARCH_QUERY_KEYS);
        window.history.replaceState(window.history.state, '', nextUrl);
        lastHydratedRuntimeUrlRef.current = nextUrl;
      } else {
        router.replace({ pathname, query: nextQuery }, undefined, { shallow: true });
      }
      pendingQueryUpdateRef.current = null;
    }, URL_SYNC_DEBOUNCE_MS);

    return () => {
      if (pendingQueryUpdateRef.current) {
        window.clearTimeout(pendingQueryUpdateRef.current);
        pendingQueryUpdateRef.current = null;
      }
    };
  }, [hydratedFromQuery, router, buildQueryParamsFromState]);
  const contextValue = useMemo<PlanningSearchContextValue>(() => ({
    searchInput,
    settledSearchInput,
    updateSearchInput,
    setSearchInput: updateSearchInput,
    searchTokens,
    applyDeterministicSearchIntent,
    addToken,
    removeToken,
    clearTokens,
    suggestions,
    selectSuggestion,
    location,
    setLocation,
    radiusKm,
    setRadius,
    mapPreview,
    toggleMapPreview,
    facetState,
    setFacetState,
    toggleCategory,
    toggleTag,
    setTags,
    clearTags,
    toggleStatus,
    setDatePreset,
    setCustomDateRange,
    setImpactRange,
    setHasDocuments,
    setHasForms,
    togglePostcode,
    clearAll,
    results,
    resultsWithMeta: sortedResultsWithMeta,
    resultCount,
    isRemoteSearchLoading,
    isRemoteSearchLoadingMore,
    remoteSearchError,
    hasRemoteSearchResultSet,
    hasMoreRemoteSearchResults,
    loadMoreRemoteSearchResults,
    facetCounts,
    activeChips,
    locationDiagnostics: null,
    radiusSearchSummary,
    buildCurrentAlertCriteria,
    saveCurrentSearch,
    savedSearches,
    deleteSavedSearch,
    applySavedSearch,
    alertsEnabled,
    setAlertsEnabled: updateAlertsEnabled,
    pendingGeocode,
    geocodeError,
    requestGeocode,
    mapFilters,
    setMapStatusFilter,
    setMapScaleFilter,
    setMapScaleRange,
    setMapCategoryFilter,
    setMapDateFilter,
    setMapCustomDateRange,
    setMapIncludeWithdrawns,
    setMapShowOnlyFavorites,
    setMapShowOnlyWorthWatching,
    resetMapFilters
  }), [
    searchInput,
    settledSearchInput,
    updateSearchInput,
    searchTokens,
    applyDeterministicSearchIntent,
    addToken,
    removeToken,
    clearTokens,
    suggestions,
    selectSuggestion,
    location,
    setLocation,
    radiusKm,
    setRadius,
    mapPreview,
    toggleMapPreview,
    facetState,
    setFacetState,
    toggleCategory,
    toggleTag,
    setTags,
    clearTags,
    toggleStatus,
      setDatePreset,
      setCustomDateRange,
      setImpactRange,
      setHasDocuments,
      setHasForms,
      togglePostcode,
    clearAll,
    results,
    sortedResultsWithMeta,
    resultCount,
    isRemoteSearchLoading,
    isRemoteSearchLoadingMore,
    hasRemoteSearchResultSet,
    hasMoreRemoteSearchResults,
    loadMoreRemoteSearchResults,
    facetCounts,
    activeChips,
    // locationDiagnostics is currently derived ad-hoc when needed; exposed as null in context for now
    radiusSearchSummary,
    buildCurrentAlertCriteria,
    saveCurrentSearch,
    savedSearches,
    deleteSavedSearch,
    applySavedSearch,
    alertsEnabled,
    updateAlertsEnabled,
    pendingGeocode,
    geocodeError,
    requestGeocode,
    mapFilters,
    setMapStatusFilter,
    setMapScaleFilter,
    setMapScaleRange,
    setMapCategoryFilter,
    setMapDateFilter,
    setMapCustomDateRange,
    setMapIncludeWithdrawns,
    setMapShowOnlyFavorites,
    setMapShowOnlyWorthWatching,
    resetMapFilters
  ]);

  return (
    <PlanningSearchContext.Provider value={contextValue}>
      {children}
    </PlanningSearchContext.Provider>
  );
};

export const usePlanningSearch = () => {
  const context = useContext(PlanningSearchContext);
  if (!context) {
    throw new Error('usePlanningSearch must be used within a PlanningSearchProvider');
  }
  return context;
};
